import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { jsPDF } from "jspdf";
import FileServiceAdmin from "./FileServiceAdmin";
import AppointmentsTab from "./admin/AppointmentsTab";
import AtelierTab from "./admin/AtelierTab";
import DepotVenteTab from "./admin/DepotVenteTab";
import TechniciensTab from "./admin/TechniciensTab";
import UsersTab from "./admin/UsersTab";
import CreateAppointmentTab from "./admin/CreateAppointmentTab";
import StatisticsTab from "./admin/StatisticsTab";
import CompletedTab from "./admin/CompletedTab";
import MessagesTab from "./admin/MessagesTab";
import PromotionsTab from "./admin/PromotionsTab";
import GalleryTab from "./admin/GalleryTab";
import ClientsTab from "./admin/ClientsTab";
import DischargesTab from "./admin/DischargesTab";
import QuotesTab from "./admin/QuotesTab";
import DynoTab from "./admin/DynoTab";
import ShopTab from "./admin/ShopTab";
import ContentTab from "./admin/ContentTab";
import WhatsAppReadyButton from "./admin/WhatsAppReadyButton";

import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Calendar,
  Trash2,
  Check,
  X,
  Lock,
  ArrowLeft,
  Phone,
  Mail,
  Car,
  FileText,
  Send,
  Circle,
  Zap,
  Plus,
  Clock,
  Ban,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  CalendarDays,
  Edit,
  RotateCcw,
  Trash,
  Paperclip,
  BarChart3,
  TrendingUp,
  Users,
  User,
  AlertTriangle,
  Bell,
  Tag,
  Image as ImageIcon,
  Search,
  History,
  StickyNote,
  Eye,
  EyeOff,
  CheckCircle,
  Activity,
  Upload,
  Download,
  Gauge,
  ShoppingBag,
  Package,
  DollarSign,
  FolderOpen,
  Save,
  RefreshCw,
  Wrench,
  Menu,
} from "lucide-react";
import { useContactInfo } from "./hooks/useContactInfo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_CALL_RE = /\/api\/(admin\/|file-service\/admin\/|blocked-slots|appointments(?!\/(available-slots|client\/)))/;

// Calendar Component for Admin
const AdminCalendar = ({ appointments, blockedSlots, onDateSelect, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  // Adjust for Monday start (0 = Monday, 6 = Sunday)
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const monthNames = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  const dayNames = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const getDateString = (day) => {
    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    return `${year}-${month}-${dayStr}`;
  };

  const getAppointmentsForDate = (day) => {
    const dateStr = getDateString(day);
    return appointments.filter((apt) => apt.date === dateStr && apt.status !== "cancelled");
  };

  const getBlockedForDate = (day) => {
    const dateStr = getDateString(day);
    return blockedSlots.filter((slot) => slot.date === dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return getDateString(day) === selectedDate;
  };

  const isWeekend = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  return (
    <div className="bg-[#121212] border border-white/10 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="p-2 hover:bg-white/10 rounded transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <h3 className="text-lg font-bold">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-white/10 rounded transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day) => (
          <div
            key={day}
            className="text-center text-xs text-white/50 font-medium py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before the first day of month */}
        {Array.from({ length: adjustedFirstDay }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}

        {/* Days of the month */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dayAppointments = getAppointmentsForDate(day);
          const dayBlocked = getBlockedForDate(day);
          const hasAppointments = dayAppointments.length > 0;
          const hasBlocked = dayBlocked.length > 0;
          const weekend = isWeekend(day);

          return (
            <button
              key={day}
              onClick={() => onDateSelect(getDateString(day))}
              className={`aspect-square p-1 rounded transition-all relative ${
                isSelected(day)
                  ? "bg-[#FF3B30] text-white"
                  : isToday(day)
                  ? "bg-white/20 text-white"
                  : weekend
                  ? "bg-white/5 text-white/30"
                  : "hover:bg-white/10 text-white"
              }`}
            >
              <span className="text-sm">{day}</span>
              
              {/* Indicators */}
              <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                {hasAppointments && (
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" title={`${dayAppointments.length} RDV`} />
                )}
                {hasBlocked && (
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-500" title={`${dayBlocked.length} bloqué(s)`} />
                )}
              </div>

              {/* Count badge */}
              {(hasAppointments || hasBlocked) && (
                <div className="absolute top-0 right-0 text-[10px] font-bold">
                  {dayAppointments.length + dayBlocked.length > 0 && (
                    <span className={`px-1 rounded ${hasAppointments ? "text-green-400" : "text-orange-400"}`}>
                      {dayAppointments.length + dayBlocked.length}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center gap-2 text-xs text-white/60">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          Rendez-vous
        </div>
        <div className="flex items-center gap-2 text-xs text-white/60">
          <div className="w-2 h-2 rounded-full bg-orange-500" />
          Bloqué
        </div>
      </div>
    </div>
  );
};

// Day Detail Component
const DayDetail = ({ date, appointments, blockedSlots, onClose, formatTime }) => {
  // Filter out cancelled appointments - only show pending and confirmed
  const dayAppointments = appointments.filter((apt) => apt.date === date && apt.status !== "cancelled");
  const dayBlocked = blockedSlots.filter((slot) => slot.date === date);

  const formatDateFull = (dateStr) => {
    const d = new Date(dateStr);
    const days = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
    const months = [
      "janvier", "février", "mars", "avril", "mai", "juin",
      "juillet", "août", "septembre", "octobre", "novembre", "décembre"
    ];
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  // All time slots for the day
  const allSlots = [];
  for (let h = 8; h < 18; h++) {
    allSlots.push(`${String(h).padStart(2, "0")}:00`);
    allSlots.push(`${String(h).padStart(2, "0")}:30`);
  }

  const getSlotContent = (time) => {
    const apt = dayAppointments.find((a) => a.time === time);
    const blocked = dayBlocked.find((b) => b.time === time);
    return { apt, blocked };
  };

  return (
    <div className="bg-[#121212] border border-white/10 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">{formatDateFull(date)}</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-white/10 rounded transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="space-y-1 max-h-[500px] overflow-y-auto">
        {allSlots.map((time) => {
          const { apt, blocked } = getSlotContent(time);
          const hasContent = apt || blocked;

          return (
            <div
              key={time}
              className={`flex items-center gap-3 p-2 rounded ${
                hasContent
                  ? apt
                    ? apt.status === "confirmed"
                      ? "bg-green-500/10 border border-green-500/30"
                      : apt.status === "cancelled"
                      ? "bg-red-500/10 border border-red-500/30 opacity-50"
                      : "bg-yellow-500/10 border border-yellow-500/30"
                    : "bg-orange-500/10 border border-orange-500/30"
                  : "bg-white/5"
              }`}
            >
              <span className="text-white/60 font-mono text-sm w-12">{time}</span>

              {apt ? (
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{apt.service_name}</span>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        apt.status === "confirmed"
                          ? "bg-green-500/20 text-green-400"
                          : apt.status === "cancelled"
                          ? "bg-red-500/20 text-red-400"
                          : "bg-yellow-500/20 text-yellow-400"
                      }`}
                    >
                      {apt.status === "confirmed"
                        ? "Confirmé"
                        : apt.status === "cancelled"
                        ? "Annulé"
                        : "En attente"}
                    </span>
                  </div>
                  <div className="text-sm text-white/60 flex items-center gap-2 flex-wrap">
                    {apt.client_name} • {apt.client_phone}
                    <WhatsAppReadyButton apt={apt} testId={`calendar-whatsapp-btn-${apt.id}`} />
                  </div>
                  {apt.client_plate && (
                    <div className="text-sm text-[#FF3B30] font-bold">
                      {apt.client_plate}
                    </div>
                  )}
                </div>
              ) : blocked ? (
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Ban size={14} className="text-orange-400" />
                    <span className="font-medium text-orange-400">
                      {blocked.service_name}
                    </span>
                    <span className="text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">
                      Bloqué
                    </span>
                  </div>
                  <div className="text-sm text-white/60">{blocked.reason}</div>
                </div>
              ) : (
                <span className="text-white/30 text-sm">Disponible</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-white/10 flex gap-4 text-sm">
        <div className="text-green-400">
          {dayAppointments.filter((a) => a.status !== "cancelled").length} RDV
        </div>
        <div className="text-orange-400">{dayBlocked.length} bloqué(s)</div>
      </div>
    </div>
  );
};

// Logo for Dyno Chart (using proxy to bypass CORS)
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/logo`;

// Dyno Chart Component - Format A4 Style Mi Systems
const DynoChart = React.forwardRef(({ dataPoints, peaks, vehicleInfo, conditions, showControls = true, compareWith = null }, ref) => {
  const canvasRef = useRef(null);
  const [chartReady, setChartReady] = useState(false);
  const contact = useContactInfo();

  useEffect(() => {
    if (!canvasRef.current || !dataPoints || dataPoints.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Load logo first, then draw everything
    const logo = new window.Image();
    logo.crossOrigin = "anonymous";
    
    const drawChart = (logoImg) => {
      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);

      // === LAYOUT CONSTANTS ===
      const headerHeight = 90;
      const rightPanelWidth = 280;
      const bottomHeight = 80;
      const graphLeft = 70;
      const graphRight = width - rightPanelWidth - 30;
      const graphTop = headerHeight + 15;
      const graphBottom = height - bottomHeight;
      const graphWidth = graphRight - graphLeft;
      const graphHeight = graphBottom - graphTop;

      // === HEADER SECTION ===
      // Left: ATR Logo
      if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
        const logoHeight = 80;
        const logoWidth = logoHeight * (logoImg.width / logoImg.height);
        ctx.drawImage(logoImg, 15, 5, logoWidth, logoHeight);
      } else {
        ctx.fillStyle = '#e63946';
        ctx.font = 'bold 28px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('AUVERGNE TECH RACING', 15, 45);
        ctx.fillStyle = '#666666';
        ctx.font = '12px Arial';
        ctx.fillText('SPÉCIALISTE REPROGRAMMATION', 15, 62);
      }

      // Center: Company info
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(contact.business_name || 'Auvergne Tech Racing', width / 2 - rightPanelWidth / 2, 28);
      ctx.font = '12px Arial';
      ctx.fillText(contact.street, width / 2 - rightPanelWidth / 2, 46);
      ctx.fillText(`${contact.postal_code} ${(contact.city || '').toUpperCase()}`, width / 2 - rightPanelWidth / 2, 62);

      // Right header: "MESURE DE PUISSANCE"
      ctx.fillStyle = '#e63946';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'right';
      ctx.fillText('MESURE DE PUISSANCE', width - rightPanelWidth - 40, 45);

      // Header line
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(10, headerHeight);
      ctx.lineTo(width - 10, headerHeight);
      ctx.stroke();

      // === WATERMARK ===
      ctx.save();
      ctx.globalAlpha = 0.05;
      ctx.fillStyle = '#e63946';
      ctx.font = 'bold 80px Arial';
      ctx.textAlign = 'center';
      ctx.translate(graphLeft + graphWidth / 2, graphTop + graphHeight / 2);
      ctx.rotate(-Math.PI / 6);
      ctx.fillText('ATR', 0, -20);
      ctx.font = 'bold 30px Arial';
      ctx.fillText('TECH RACING', 0, 25);
      ctx.restore();

      // === RIGHT PANEL - RUN DETAILS ===
      const panelX = width - rightPanelWidth - 10;
      const panelY = headerHeight + 10;

      // Main info box
      ctx.strokeStyle = '#dddddd';
      ctx.lineWidth = 1;
      ctx.strokeRect(panelX, panelY, rightPanelWidth, 180);
      
      // Color indicator bar
      ctx.fillStyle = '#e63946';
      ctx.fillRect(panelX, panelY, 6, 180);
      
      // Vehicle info
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`${vehicleInfo.brand || 'Marque'} ${vehicleInfo.model || 'Modèle'}`, panelX + 15, panelY + 25);
      
      // Year
      ctx.font = '11px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText(vehicleInfo.year || '', panelX + 15, panelY + 42);
      
      // Date
      ctx.textAlign = 'right';
      const dateStr = conditions.date ? new Date(conditions.date).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR');
      ctx.fillText(dateStr, panelX + rightPanelWidth - 10, panelY + 25);
      
      // Config name
      ctx.textAlign = 'left';
      ctx.font = '12px Arial';
      ctx.fillStyle = '#e63946';
      ctx.fillText(vehicleInfo.config || 'Configuration', panelX + 15, panelY + 62);
      
      // Horizontal separator
      ctx.strokeStyle = '#eeeeee';
      ctx.beginPath();
      ctx.moveTo(panelX + 10, panelY + 75);
      ctx.lineTo(panelX + rightPanelWidth - 10, panelY + 75);
      ctx.stroke();
      
      // Power result
      ctx.fillStyle = '#e63946';
      ctx.font = 'bold 22px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`${(peaks.maxPower || peaks.max_power)?.toFixed(1) || 0} ch`, panelX + 15, panelY + 105);
      ctx.font = '11px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText(`@ ${peaks.maxPowerRpm || peaks.max_power_rpm || 0} tr/min`, panelX + 15, panelY + 122);
      
      // Torque result
      ctx.fillStyle = '#2196F3';
      ctx.font = 'bold 22px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(`${(peaks.maxTorque || peaks.max_torque)?.toFixed(1) || 0} Nm`, panelX + rightPanelWidth - 15, panelY + 105);
      ctx.font = '11px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText(`@ ${peaks.maxTorqueRpm || peaks.max_torque_rpm || 0} tr/min`, panelX + rightPanelWidth - 15, panelY + 122);
      
      // Labels
      ctx.fillStyle = '#888888';
      ctx.font = '10px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('PUISSANCE AUX ROUES', panelX + 15, panelY + 145);
      ctx.textAlign = 'right';
      ctx.fillText('COUPLE AUX ROUES', panelX + rightPanelWidth - 15, panelY + 145);
      
      // DIN norm
      ctx.textAlign = 'center';
      ctx.fillStyle = '#888888';
      ctx.font = '10px Arial';
      ctx.fillText('Norme DIN 70020', panelX + rightPanelWidth / 2, panelY + 170);

      // === CONDITIONS BOX ===
      const condBoxY = panelY + 195;
      ctx.strokeStyle = '#dddddd';
      ctx.strokeRect(panelX, condBoxY, rightPanelWidth, 70);
      
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('CONDITIONS ATMOSPHÉRIQUES', panelX + 10, condBoxY + 18);
      
      ctx.font = '11px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText(`Température: ${conditions.temperature || 20} °C`, panelX + 10, condBoxY + 38);
      ctx.fillText(`Pression: ${conditions.pressure || 1013} hPa`, panelX + 10, condBoxY + 55);
      ctx.textAlign = 'right';
      ctx.fillText(`Humidité: ${conditions.humidity || 50} %`, panelX + rightPanelWidth - 10, condBoxY + 38);

      // === GRAPH AREA ===
      ctx.fillStyle = '#f8f8f8';
      ctx.fillRect(graphLeft, graphTop, graphWidth, graphHeight);
      
      ctx.strokeStyle = '#333333';
      ctx.lineWidth = 1;
      ctx.strokeRect(graphLeft, graphTop, graphWidth, graphHeight);

      // Data ranges
      const rpmValues = dataPoints.map(p => p.rpm);
      const powerValues = dataPoints.map(p => p.power);
      const torqueValues = dataPoints.map(p => p.torque);
      
      const minRpm = Math.floor(Math.min(...rpmValues) / 250) * 250;
      const maxRpm = Math.ceil(Math.max(...rpmValues) / 250) * 250;
      const maxPower = Math.ceil(Math.max(...powerValues) / 20) * 20 + 20;
      const maxTorque = Math.ceil(Math.max(...torqueValues) / 25) * 25 + 25;

      // Scale functions
      const scaleX = (rpm) => graphLeft + ((rpm - minRpm) / (maxRpm - minRpm)) * graphWidth;
      const scalePower = (p) => graphBottom - (p / maxPower) * graphHeight;
      const scaleTorque = (t) => graphBottom - (t / maxTorque) * graphHeight;

      // Draw detailed grid
      ctx.strokeStyle = '#dddddd';
      ctx.lineWidth = 0.5;

      // Vertical grid (RPM)
      for (let rpm = minRpm; rpm <= maxRpm; rpm += 250) {
        const x = scaleX(rpm);
        ctx.beginPath();
        ctx.moveTo(x, graphTop);
        ctx.lineTo(x, graphBottom);
        ctx.stroke();
      }

      // Horizontal grid
      for (let p = 0; p <= maxPower; p += 10) {
        const y = scalePower(p);
        ctx.beginPath();
        ctx.moveTo(graphLeft, y);
        ctx.lineTo(graphRight, y);
        ctx.stroke();
      }

      // Y-axis labels - Power (ch)
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('ch', graphLeft - 35, graphTop - 8);
      
      ctx.font = '9px Arial';
      ctx.textAlign = 'right';
      for (let p = 0; p <= maxPower; p += 20) {
        ctx.fillText(p.toString(), graphLeft - 8, scalePower(p) + 3);
      }

      // Y-axis labels - Torque (Nm)
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Nm', graphRight + 30, graphTop - 8);
      
      ctx.font = '9px Arial';
      ctx.textAlign = 'left';
      for (let t = 0; t <= maxTorque; t += 25) {
        ctx.fillText(t.toString(), graphRight + 8, scaleTorque(t) + 3);
      }

      // X-axis labels (RPM)
      ctx.textAlign = 'center';
      ctx.font = '9px Arial';
      for (let rpm = minRpm; rpm <= maxRpm; rpm += 500) {
        ctx.fillText(rpm.toString(), scaleX(rpm), graphBottom + 15);
      }
      ctx.font = 'bold 10px Arial';
      ctx.fillText('tr/min', graphRight + 20, graphBottom + 15);

      // === DRAW CURVES ===
      // Torque curve (BLUE, dashed)
      ctx.strokeStyle = '#2196F3';
      ctx.lineWidth = 2.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      dataPoints.forEach((point, i) => {
        const x = scaleX(point.rpm);
        const y = scaleTorque(point.torque);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.setLineDash([]);

      // Power curve (RED, solid)
      ctx.strokeStyle = '#e63946';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      dataPoints.forEach((point, i) => {
        const x = scaleX(point.rpm);
        const y = scalePower(point.power);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();

      // === FOOTER ===
      ctx.fillStyle = '#888888';
      ctx.font = '9px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(dateStr, 15, height - 15);
      
      ctx.textAlign = 'center';
      ctx.fillText('Mesures effectuées sur route avec équipement RACELOGIC', graphLeft + graphWidth / 2, height - 15);
      
      ctx.textAlign = 'right';
      ctx.fillText('© Auvergne Tech Racing', width - 15, height - 15);
      
      setChartReady(true);
    };

    logo.onload = () => drawChart(logo);
    logo.onerror = () => drawChart(null);
    logo.src = `${process.env.REACT_APP_BACKEND_URL}/api/logo`;

  }, [dataPoints, peaks, vehicleInfo, conditions, compareWith, contact.street, contact.postal_code, contact.city, contact.business_name]);

  const handleDownloadPNG = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    const vehicleName = `${vehicleInfo.brand}_${vehicleInfo.model}`.replace(/\s+/g, '_');
    link.download = `ATR_Dyno_${vehicleName}_${new Date().toISOString().split('T')[0]}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  const handleDownloadPDF = () => {
    if (!canvasRef.current) return;
    const vehicleName = `${vehicleInfo.brand}_${vehicleInfo.model}`.replace(/\s+/g, '_');
    const fileName = `ATR_Dyno_${vehicleName}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const imgData = canvasRef.current.toDataURL('image/png', 1.0);
    pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
    pdf.save(fileName);
  };

  return (
    <div ref={ref} className="bg-white rounded-lg overflow-hidden shadow-lg border border-gray-200">
      <canvas 
        ref={canvasRef} 
        width={1240} 
        height={1754}
        className="w-full h-auto"
      />
      {showControls && (
        <div className="flex gap-3 justify-center p-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 bg-[#e63946] hover:bg-[#d62836] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-sm"
          >
            📄 Télécharger PDF
          </button>
          <button
            onClick={handleDownloadPNG}
            className="flex items-center gap-2 bg-[#2196F3] hover:bg-[#1976D2] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-sm"
          >
            🖼️ Télécharger PNG
          </button>
        </div>
      )}
    </div>
  );
});

DynoChart.displayName = 'DynoChart';

// Dyno Comparison Chart Component - A4 Format (Landscape: 297mm x 210mm)
const DynoCompareChart = React.forwardRef(({ runs, onDownload }, ref) => {
  const canvasRef = useRef(null);
  const contact = useContactInfo();
  
  // Color palette for runs - matching Mi Systems style
  const runColors = ['#000000', '#2196F3', '#e63946', '#4CAF50', '#FF9800'];

  useEffect(() => {
    if (!canvasRef.current || !runs || runs.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Load logo first, then draw
    const logo = new window.Image();
    logo.crossOrigin = "anonymous";

    const drawChart = (logoImg) => {
      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);

      // === LAYOUT CONSTANTS ===
      const headerHeight = 80;
      const rightPanelWidth = 320;
      const bottomHeight = 100;
      const graphLeft = 70;
      const graphRight = width - rightPanelWidth - 30;
      const graphTop = headerHeight + 20;
      const graphBottom = height - bottomHeight;
      const graphWidth = graphRight - graphLeft;
      const graphHeight = graphBottom - graphTop;

      // === HEADER SECTION ===
      // Left: ATR Logo (bigger)
      if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
        const logoHeight = 90;
        const logoWidth = logoHeight * (logoImg.width / logoImg.height);
        ctx.drawImage(logoImg, 15, 5, logoWidth, logoHeight);
      } else {
        ctx.fillStyle = '#e63946';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('AUVERGNE TECH RACING', 15, 50);
        ctx.fillStyle = '#666666';
        ctx.font = '14px Arial';
        ctx.fillText('SPÉCIALISTE REPROGRAMMATION', 15, 68);
      }

      // Center: Company info (bigger)
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(contact.business_name || 'Auvergne Tech Racing', width / 2 - rightPanelWidth / 2, 30);
      ctx.font = '14px Arial';
      ctx.fillText(contact.street, width / 2 - rightPanelWidth / 2, 50);
      ctx.fillText(`${contact.postal_code} ${(contact.city || '').toUpperCase()}`, width / 2 - rightPanelWidth / 2, 68);

      // Right header: "MESURE DE PUISSANCE"
      ctx.fillStyle = '#e63946';
      ctx.font = 'bold 15px Arial';
      ctx.textAlign = 'right';
      ctx.fillText('MESURE DE PUISSANCE', width - rightPanelWidth - 40, 50);

      // Header line
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(10, headerHeight);
      ctx.lineTo(width - 10, headerHeight);
      ctx.stroke();

      // === WATERMARK ===
      ctx.save();
      ctx.globalAlpha = 0.06;
      ctx.fillStyle = '#e63946';
      ctx.font = 'bold 100px Arial';
      ctx.textAlign = 'center';
      ctx.translate(graphLeft + graphWidth / 2, graphTop + graphHeight / 2);
      ctx.rotate(-Math.PI / 6);
      ctx.fillText('ATR', 0, -30);
      ctx.font = 'bold 40px Arial';
      ctx.fillText('TECH RACING', 0, 30);
      ctx.restore();

      // === RIGHT PANEL - RUN DETAILS ===
      const panelX = width - rightPanelWidth - 10;
      const panelY = headerHeight + 10;
      const runBoxHeight = Math.min(130, (height - headerHeight - bottomHeight - 40) / runs.length);

      runs.forEach((run, i) => {
        const boxY = panelY + (i * runBoxHeight);
        const color = runColors[i % runColors.length];
        
        // Box border
        ctx.strokeStyle = '#dddddd';
        ctx.lineWidth = 1;
        ctx.strokeRect(panelX, boxY, rightPanelWidth, runBoxHeight - 5);
        
        // Color indicator bar on left
        ctx.fillStyle = color;
        ctx.fillRect(panelX, boxY, 6, runBoxHeight - 5);
        
        // Vehicle info
        ctx.fillStyle = '#333333';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(`${run.vehicle_brand} ${run.vehicle_model}`, panelX + 15, boxY + 18);
        
        // Date
        ctx.font = '10px Arial';
        ctx.textAlign = 'right';
        ctx.fillStyle = '#666666';
        ctx.fillText(run.run_date || '', panelX + rightPanelWidth - 10, boxY + 18);
        
        // Config name
        ctx.textAlign = 'left';
        ctx.font = '11px Arial';
        ctx.fillStyle = '#888888';
        ctx.fillText(run.config_name || `Passage ${i + 1}`, panelX + 15, boxY + 35);
        
        // Horizontal separator
        ctx.strokeStyle = '#eeeeee';
        ctx.beginPath();
        ctx.moveTo(panelX + 10, boxY + 45);
        ctx.lineTo(panelX + rightPanelWidth - 10, boxY + 45);
        ctx.stroke();
        
        // Power result
        ctx.fillStyle = color;
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(`${run.max_power?.toFixed(1) || 0} ch`, panelX + 15, boxY + 65);
        ctx.font = '10px Arial';
        ctx.fillStyle = '#666666';
        ctx.fillText(`- ${run.max_power_rpm || 0} tr/min`, panelX + 85, boxY + 65);
        
        // Torque result
        ctx.fillStyle = color;
        ctx.font = 'bold 14px Arial';
        ctx.fillText(`${run.max_torque?.toFixed(1) || 0} Nm`, panelX + 170, boxY + 65);
        ctx.font = '10px Arial';
        ctx.fillStyle = '#666666';
        ctx.fillText(`- ${run.max_torque_rpm || 0} tr/min`, panelX + 235, boxY + 65);
        
        // "Aux roues" and "DIN" labels
        ctx.fillStyle = '#888888';
        ctx.font = '10px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('Aux roues', panelX + 15, boxY + 85);
        ctx.textAlign = 'right';
        ctx.fillText('DIN', panelX + rightPanelWidth - 10, boxY + 85);
        
        // Gain vs first run
        if (i > 0 && runs[0]) {
          const powerGain = run.max_power - runs[0].max_power;
          const torqueGain = run.max_torque - runs[0].max_torque;
          ctx.fillStyle = powerGain >= 0 ? '#2e7d32' : '#c62828';
          ctx.font = 'bold 11px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(`${powerGain >= 0 ? '+' : ''}${powerGain.toFixed(1)} ch / ${torqueGain >= 0 ? '+' : ''}${torqueGain.toFixed(1)} Nm`, panelX + rightPanelWidth / 2, boxY + runBoxHeight - 15);
        }
      });

      // === GRAPH AREA ===
      // Graph background
      ctx.fillStyle = '#f8f8f8';
      ctx.fillRect(graphLeft, graphTop, graphWidth, graphHeight);
      
      // Graph border
      ctx.strokeStyle = '#333333';
      ctx.lineWidth = 1;
      ctx.strokeRect(graphLeft, graphTop, graphWidth, graphHeight);

      // Find global data ranges
      let allRpmValues = [];
      let allPowerValues = [];
      let allTorqueValues = [];
      
      runs.forEach(run => {
        if (run.data_points) {
          allRpmValues.push(...run.data_points.map(p => p.rpm));
          allPowerValues.push(...run.data_points.map(p => p.power));
          allTorqueValues.push(...run.data_points.map(p => p.torque));
        }
      });

      const minRpm = Math.floor(Math.min(...allRpmValues) / 250) * 250;
      const maxRpm = Math.ceil(Math.max(...allRpmValues) / 250) * 250;
      const maxPower = Math.ceil(Math.max(...allPowerValues) / 20) * 20 + 20;
      const maxTorque = Math.ceil(Math.max(...allTorqueValues) / 25) * 25 + 25;

      // Scale functions
      const scaleX = (rpm) => graphLeft + ((rpm - minRpm) / (maxRpm - minRpm)) * graphWidth;
      const scalePower = (p) => graphBottom - (p / maxPower) * graphHeight;
      const scaleTorque = (t) => graphBottom - (t / maxTorque) * graphHeight;

      // Draw detailed grid
      ctx.strokeStyle = '#dddddd';
      ctx.lineWidth = 0.5;

      // Vertical grid lines (RPM) - every 250
      for (let rpm = minRpm; rpm <= maxRpm; rpm += 250) {
        const x = scaleX(rpm);
        ctx.beginPath();
        ctx.moveTo(x, graphTop);
        ctx.lineTo(x, graphBottom);
        ctx.stroke();
      }

      // Horizontal grid lines (Power) - every 10 ch
      const powerStep = 10;
      for (let p = 0; p <= maxPower; p += powerStep) {
        const y = scalePower(p);
        ctx.beginPath();
        ctx.moveTo(graphLeft, y);
        ctx.lineTo(graphRight, y);
        ctx.stroke();
      }

      // Y-axis labels - Power (ch) on LEFT
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('ch', graphLeft - 35, graphTop - 8);
      
      ctx.font = '9px Arial';
      ctx.textAlign = 'right';
      for (let p = 0; p <= maxPower; p += 20) {
        ctx.fillText(p.toString(), graphLeft - 8, scalePower(p) + 3);
      }

      // Y-axis labels - Torque (Nm) on RIGHT
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Nm', graphRight + 35, graphTop - 8);
      
      ctx.font = '9px Arial';
      ctx.textAlign = 'left';
      const torqueStep = 25;
      for (let t = 0; t <= maxTorque; t += torqueStep) {
        ctx.fillText(t.toString(), graphRight + 8, scaleTorque(t) + 3);
      }

      // X-axis labels (RPM)
      ctx.textAlign = 'center';
      ctx.font = '9px Arial';
      for (let rpm = minRpm; rpm <= maxRpm; rpm += 500) {
        ctx.fillText(rpm.toString(), scaleX(rpm), graphBottom + 15);
      }
      ctx.font = 'bold 11px Arial';
      ctx.fillText('tr/min', graphRight + 20, graphBottom + 15);

      // === DRAW CURVES ===
      runs.forEach((run, runIndex) => {
        if (!run.data_points || run.data_points.length === 0) return;
        
        const color = runColors[runIndex % runColors.length];
        const lineWidth = runIndex === 0 ? 2.5 : 2;

        // Draw TORQUE curve (dashed)
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        run.data_points.forEach((point, i) => {
          const x = scaleX(point.rpm);
          const y = scaleTorque(point.torque);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.setLineDash([]);

        // Draw POWER curve (solid)
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        run.data_points.forEach((point, i) => {
          const x = scaleX(point.rpm);
          const y = scalePower(point.power);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
      });

      // === BOTTOM CONDITIONS SECTION ===
      const condY = height - bottomHeight + 20;
      
      // Conditions box
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      ctx.strokeRect(graphLeft, condY, graphWidth * 0.5, bottomHeight - 30);
      
      ctx.fillStyle = '#333333';
      ctx.font = '10px Arial';
      ctx.textAlign = 'left';
      
      runs.forEach((run, i) => {
        const color = runColors[i % runColors.length];
        const lineY = condY + 15 + (i * 18);
        
        // Color indicator
        ctx.fillStyle = color;
        ctx.fillRect(graphLeft + 10, lineY - 8, 12, 12);
        
        // Conditions text
        ctx.fillStyle = '#333333';
        ctx.fillText(
          `${run.temperature || 20} °C   ${run.pressure || 1013} hPa   ${run.humidity || 50} %`,
          graphLeft + 30, lineY
        );
      });

      // === FOOTER ===
      ctx.fillStyle = '#888888';
      ctx.font = '9px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Mesures effectuées sur route avec équipement RACELOGIC • Norme DIN 70020', graphLeft + graphWidth / 2, height - 15);
      
      ctx.textAlign = 'right';
      ctx.fillText('© Auvergne Tech Racing', width - 20, height - 15);
      
      ctx.textAlign = 'left';
      ctx.fillText(new Date().toLocaleDateString('fr-FR'), 20, height - 15);
    };

    logo.onload = () => drawChart(logo);
    logo.onerror = () => drawChart(null);
    logo.src = `${process.env.REACT_APP_BACKEND_URL}/api/logo`;

  }, [runs, contact.street, contact.postal_code, contact.city, contact.business_name]);

  const handleDownload = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    const vehicleName = runs[0] ? `${runs[0].vehicle_brand}_${runs[0].vehicle_model}` : 'comparaison';
    link.download = `ATR_Comparaison_${vehicleName}_${new Date().toISOString().split('T')[0]}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  const handleDownloadPDF = () => {
    if (!canvasRef.current) return;
    const vehicleName = runs[0] ? `${runs[0].vehicle_brand}_${runs[0].vehicle_model}` : 'comparaison';
    const fileName = `ATR_Comparaison_${vehicleName}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    // Create PDF in A4 landscape
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // A4 landscape: 297mm x 210mm
    const imgData = canvasRef.current.toDataURL('image/png', 1.0);
    pdf.addImage(imgData, 'PNG', 0, 0, 297, 210);
    pdf.save(fileName);
  };

  const handlePrint = () => {
    if (!canvasRef.current) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    const windowContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Comparaison Banc de Puissance - Auvergne Tech Racing</title>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            body { margin: 0; display: flex; justify-content: center; align-items: center; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          <img src="${dataUrl}" />
        </body>
      </html>
    `;
    const printWindow = window.open('', '', 'width=1200,height=800');
    printWindow.document.write(windowContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <div ref={ref} className="space-y-4">
      <div className="bg-white rounded-lg overflow-hidden shadow-lg">
        <canvas 
          ref={canvasRef} 
          width={1754} 
          height={1400}
          className="w-full h-auto"
        />
      </div>
      <div className="flex gap-3 justify-center flex-wrap">
        <button
          onClick={handleDownloadPDF}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
        >
          📄 Télécharger PDF (A4)
        </button>
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
        >
          🖼️ Télécharger PNG
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
        >
          🖨️ Imprimer
        </button>
      </div>
    </div>
  );
});

DynoCompareChart.displayName = 'DynoCompareChart';

const Admin = () => {
  const contact = useContactInfo();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  // Role: "admin" | "secretary" — drives the tab filter.
  const [role, setRole] = useState("admin");
  const [currentUser, setCurrentUser] = useState("admin");
  const [appointments, setAppointments] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [confirmingId, setConfirmingId] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("appointments");
  const [contentInitialKey, setContentInitialKey] = useState(null);
  // Mobile: controls the tab-picker drawer so the 18-tab admin fits a 375px screen.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ date: "", time: "", client_plate: "", service_id: "" });
  const [emailModal, setEmailModal] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [directEmailForm, setDirectEmailForm] = useState({ 
    to_email: "", 
    to_name: "", 
    subject: "", 
    message: "" 
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [emailAttachment, setEmailAttachment] = useState(null);
  const emailFileInputRef = useRef(null);
  const fileInputRef = useRef({});
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Phase 2 states
  const [promotions, setPromotions] = useState([]);
  const [promoForm, setPromoForm] = useState({ title: "", description: "", discount: "", valid_until: "", show_banner: true, show_section: true });
  // When set, the "Nouvelle promotion" form becomes an edit form for this promo id.
  const [editingPromoId, setEditingPromoId] = useState(null);
  const [editingPromo, setEditingPromo] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryForm, setGalleryForm] = useState({ url: "", caption: "", order: 0 });
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClientHistory, setSelectedClientHistory] = useState(null);
  const [clientHistory, setClientHistory] = useState({ appointments: [], quotes: [], messages: [], notes: [] });
  const [newNote, setNewNote] = useState("");
  const [editClientForm, setEditClientForm] = useState({ name: "", email: "", phone: "", plates: "" });
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ name: "", email: "", phone: "", plates: "" });
  const [isEditingClient, setIsEditingClient] = useState(false);

  // Dyno states
  const [dynoRuns, setDynoRuns] = useState([]);
  const [selectedDynoRun, setSelectedDynoRun] = useState(null);
  const [dynoForm, setDynoForm] = useState({
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_year: "",
    vehicle_plate: "",
    config_name: "",
    temperature: 20,
    pressure: 1013,
    humidity: 50,
    correction_norm: "DIN 70020",
    correction_coefficient: 1.0,
    client_email: "",
    client_name: "",
    client_phone: "",
    run_date: new Date().toISOString().split('T')[0],
    notes: ""
  });
  const [dynoDataPoints, setDynoDataPoints] = useState([]);
  const [dynoPeaks, setDynoPeaks] = useState({ max_power: 0, max_power_rpm: 0, max_torque: 0, max_torque_rpm: 0 });
  const [dynoFileUploaded, setDynoFileUploaded] = useState(false);
  const [dynoLoading, setDynoLoading] = useState(false);
  const [dynoEmailModal, setDynoEmailModal] = useState(null);
  const [dynoEmailForm, setDynoEmailForm] = useState({ to_email: "", to_name: "", subject: "", message: "" });
  const dynoFileInputRef = useRef(null);
  const dynoChartRef = useRef(null);
  const [dynoCompareSelection, setDynoCompareSelection] = useState([]);
  const [dynoCompareModal, setDynoCompareModal] = useState(false);
  const dynoCompareChartRef = useRef(null);

  // Shop states
  const [shopProducts, setShopProducts] = useState([]);
  const [shopOrders, setShopOrders] = useState([]);
  const [shopCategories, setShopCategories] = useState([]);
  const [shopCustomers, setShopCustomers] = useState([]);
  const [shopCustomerSearch, setShopCustomerSearch] = useState("");
  const [selectedShopCustomer, setSelectedShopCustomer] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ id: "", name: "", icon: "Package" });
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [shopProductForm, setShopProductForm] = useState({
    name: "", description: "", short_description: "", category: "turbos",
    brand: "", reference: "", price: "", original_price: "", shipping_cost: "", stock: "",
    images: [], specifications: {}, featured: false, active: true, show_stock: true
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [shopLoading, setShopLoading] = useState(false);
  const [shopImageUrl, setShopImageUrl] = useState("");
  const [shopSpecKey, setShopSpecKey] = useState("");
  const [shopSpecValue, setShopSpecValue] = useState("");
  const [importUrl, setImportUrl] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [importVariants, setImportVariants] = useState(null); // {has_variants, variants, name, etc.}
  const [selectedVariant, setSelectedVariant] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [orderStatusLoading, setOrderStatusLoading] = useState(false);
  
  // Atelier states
  const [atelierIntakes, setAtelierIntakes] = useState([]);
  const [atelierSearch, setAtelierSearch] = useState("");
  const [atelierStatusFilter, setAtelierStatusFilter] = useState("");
  const [selectedAtelierIntake, setSelectedAtelierIntake] = useState(null);
  const [technicianPassword, setTechnicianPassword] = useState("");
  const [newTechnicianPassword, setNewTechnicianPassword] = useState("");
  
  // Discharges states
  const [discharges, setDischarges] = useState([]);
  const [dischargeSearch, setDischargeSearch] = useState("");
  const [selectedDischarge, setSelectedDischarge] = useState(null);
  const [showDischargeForm, setShowDischargeForm] = useState(false);
  const [dischargeContent, setDischargeContent] = useState(null);
  const [dischargeForm, setDischargeForm] = useState({
    client_name: "",
    client_address: "",
    client_phone: "",
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_plate: "",
    vehicle_vin: "",
    vehicle_mileage: "",
    intervention_type: [],
    other_intervention: "",
    signed: false,
    signature_date: ""
  });
  const [editingDischargeId, setEditingDischargeId] = useState(null);
  
  // Quotes state
  const [quotes, setQuotes] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    description: "", vehicle_info: "", amount_ht: "", amount_ttc: "",
    external_quote_number: "", notes: ""
  });
  const [quotePdfFile, setQuotePdfFile] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [showCreateAppointmentFromQuote, setShowCreateAppointmentFromQuote] = useState(null); // quote object
  const [appointmentFromQuoteForm, setAppointmentFromQuoteForm] = useState({
    date: "", time: "", service: "", duration: 60
  });

  // Technicians list
  const technicians = ["Kevin", "Antoine", "Abder"];

  // Available time slots for rescheduling
  const timeSlots = [];
  for (let h = 8; h < 18; h++) {
    timeSlots.push(`${String(h).padStart(2, "0")}:00`);
    timeSlots.push(`${String(h).padStart(2, "0")}:30`);
  }

  // Form for blocking slots
  const [blockForm, setBlockForm] = useState({
    date: "",
    time: "",
    service_id: "",
    reason: "Réservation directe",
  });

  // Form for manual appointments
  const [manualForm, setManualForm] = useState({
    service_id: "",
    service_name: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    client_plate: "",
    date: "",
    time: "",
    duration: 60,
    technician: "",
    notes: "",
  });

  // Duration options
  const durationOptions = [30, 45, 60, 90, 120, 150, 180, 240];

  // Set up axios interceptors ONCE when Admin.js mounts:
  // - Request: inject X-Admin-Token header on every /api/admin/* call
  // - Response: force logout on 401 from any /api/admin/* call
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      try {
        const url = String(config.url || "");
        const isAdminCall = ADMIN_CALL_RE.test(url) && !url.includes("/api/admin/login");
        if (isAdminCall) {
          const tk = localStorage.getItem("atr_admin_token");
          if (tk) {
            config.headers = config.headers || {};
            config.headers["X-Admin-Token"] = tk;
          }
        }
      } catch (_) { /* never break the request */ }
      return config;
    });
    const resId = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const url = String(error?.config?.url || "");
        const status = error?.response?.status;
        const isAdminCall = ADMIN_CALL_RE.test(url) && !url.includes("/api/admin/login");
        if (status === 401 && isAdminCall) {
          localStorage.removeItem("atr_admin_token");
          sessionStorage.removeItem("atr_admin_auth");
          setIsAuthenticated(false);
          toast.error("Session expirée — veuillez vous reconnecter");
        }
        return Promise.reject(error);
      },
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  // Check if already authenticated (validate token server-side on mount)
  useEffect(() => {
    const tk = localStorage.getItem("atr_admin_token");
    if (!tk) return;
    axios.get(`${API}/admin/session`, { headers: { "X-Admin-Token": tk } })
      .then((res) => {
        const r = res.data?.role || "admin";
        const u = res.data?.username || "admin";
        setRole(r);
        setCurrentUser(u);
        localStorage.setItem("atr_admin_role", r);
        localStorage.setItem("atr_admin_username", u);
        // Secretary should land on "appointments" by default, not any admin-only tab.
        if (r === "secretary") setActiveTab("appointments");
        setIsAuthenticated(true);
        sessionStorage.setItem("atr_admin_auth", "true");
      })
      .catch(() => {
        // Token expired or invalid — force re-login
        localStorage.removeItem("atr_admin_token");
        localStorage.removeItem("atr_admin_role");
        localStorage.removeItem("atr_admin_username");
        sessionStorage.removeItem("atr_admin_auth");
      });
  }, []);

  // Fetch data
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllAppointments();
      fetchAllBlockedSlots();
      fetchServices();
      fetchClients();
      // Admin-only data — skipped for secretary to avoid 403 noise.
      if (role === "admin") {
        fetchStatistics();
        fetchPromotions();
        fetchGallery();
      }
    }
  }, [isAuthenticated, role]);

  // Fetch filtered data when filterDate changes
  useEffect(() => {
    if (isAuthenticated && filterDate) {
      fetchAppointments();
      fetchBlockedSlots();
    }
  }, [filterDate]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/statistics`);
      setStatistics(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast.error("Erreur de chargement des statistiques");
    } finally {
      setStatsLoading(false);
    }
  };

  // Dyno functions
  const fetchDynoRuns = async () => {
    try {
      const response = await axios.get(`${API}/admin/dyno`);
      setDynoRuns(response.data);
    } catch (error) {
      console.error("Error fetching dyno runs:", error);
    }
  };

  // Shop functions
  const fetchShopProducts = async () => {
    try {
      const response = await axios.get(`${API}/shop/products`);
      setShopProducts(response.data);
    } catch (error) {
      console.error("Error fetching shop products:", error);
    }
  };

  const fetchShopOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/shop/orders`);
      setShopOrders(response.data);
    } catch (error) {
      console.error("Error fetching shop orders:", error);
    }
  };

  const fetchShopCategories = async () => {
    try {
      const response = await axios.get(`${API}/shop/categories`);
      setShopCategories(response.data);
    } catch (error) {
      console.error("Error fetching shop categories:", error);
    }
  };

  const fetchShopCustomers = async (search = "") => {
    try {
      const url = search ? `${API}/admin/shop/customers?search=${encodeURIComponent(search)}` : `${API}/admin/shop/customers`;
      const response = await axios.get(url);
      setShopCustomers(response.data);
    } catch (error) {
      console.error("Error fetching shop customers:", error);
    }
  };

  // Atelier functions
  const fetchAtelierIntakes = async (status = "", search = "") => {
    try {
      let url = `${API}/admin/atelier/intakes`;
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setAtelierIntakes(response.data);
    } catch (error) {
      console.error("Error fetching atelier intakes:", error);
    }
  };

  const fetchTechnicianPassword = async () => {
    try {
      const response = await axios.get(`${API}/admin/atelier/password`);
      setTechnicianPassword(response.data.password);
    } catch (error) {
      console.error("Error fetching technician password:", error);
    }
  };

  const updateTechnicianPassword = async () => {
    if (!newTechnicianPassword || newTechnicianPassword.length < 4) {
      toast.error("Le mot de passe doit contenir au moins 4 caractères");
      return;
    }
    try {
      await axios.put(`${API}/admin/atelier/password`, { password: newTechnicianPassword });
      setTechnicianPassword(newTechnicianPassword);
      setNewTechnicianPassword("");
      toast.success("Mot de passe technicien mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du mot de passe");
    }
  };

  const deleteAtelierIntake = async (id) => {
    if (!window.confirm("Supprimer cette fiche de prise en charge ?")) return;
    try {
      await axios.delete(`${API}/admin/atelier/intake/${id}`);
      toast.success("Fiche supprimée");
      fetchAtelierIntakes(atelierStatusFilter, atelierSearch);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Discharge functions
  const fetchDischargeContent = async () => {
    try {
      const response = await axios.get(`${API}/content/discharge`);
      setDischargeContent(response.data);
    } catch (error) {
      // fall back to hard-coded defaults inside the PDF generator
      setDischargeContent(null);
    }
  };

  const fetchDischarges = async (search = "") => {
    try {
      const response = await axios.get(`${API}/admin/discharges?search=${search}`);
      setDischarges(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des décharges");
    }
  };

  const handleCreateDischarge = async () => {
    if (!dischargeForm.client_name || !dischargeForm.vehicle_plate) {
      toast.error("Le nom du client et la plaque sont obligatoires");
      return;
    }
    try {
      const data = {
        ...dischargeForm,
        vehicle_mileage: parseInt(dischargeForm.vehicle_mileage) || 0
      };
      if (editingDischargeId) {
        await axios.put(`${API}/admin/discharges/${editingDischargeId}`, data);
        toast.success("Décharge mise à jour");
      } else {
        await axios.post(`${API}/admin/discharges`, data);
        toast.success("Décharge créée");
      }
      setShowDischargeForm(false);
      setEditingDischargeId(null);
      setDischargeForm({
        client_name: "", client_address: "", client_phone: "",
        vehicle_brand: "", vehicle_model: "", vehicle_plate: "",
        vehicle_vin: "", vehicle_mileage: "", intervention_type: [],
        other_intervention: "", signed: false, signature_date: ""
      });
      fetchDischarges(dischargeSearch);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const editDischarge = (discharge) => {
    setDischargeForm({
      client_name: discharge.client_name || "",
      client_address: discharge.client_address || "",
      client_phone: discharge.client_phone || "",
      vehicle_brand: discharge.vehicle_brand || "",
      vehicle_model: discharge.vehicle_model || "",
      vehicle_plate: discharge.vehicle_plate || "",
      vehicle_vin: discharge.vehicle_vin || "",
      vehicle_mileage: discharge.vehicle_mileage?.toString() || "",
      intervention_type: discharge.intervention_type || [],
      other_intervention: discharge.other_intervention || "",
      signed: discharge.signed || false,
      signature_date: discharge.signature_date || ""
    });
    setEditingDischargeId(discharge.id);
    setShowDischargeForm(true);
  };

  const deleteDischarge = async (id) => {
    if (!window.confirm("Supprimer cette décharge ?")) return;
    try {
      await axios.delete(`${API}/admin/discharges/${id}`);
      toast.success("Décharge supprimée");
      fetchDischarges(dischargeSearch);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const signDischarge = async (id) => {
    try {
      await axios.put(`${API}/admin/discharges/${id}/sign`);
      toast.success("Décharge marquée comme signée");
      fetchDischarges(dischargeSearch);
    } catch (error) {
      toast.error("Erreur lors de la signature");
    }
  };

  // Generate PDF for discharge
  const generateDischargePDF = (discharge) => {
    const c = dischargeContent || {};
    const labels = c.labels || {};
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Colors
    const primaryRed = [200, 30, 30];
    const black = [0, 0, 0];
    const darkGray = [50, 50, 50];
    const lightGray = [120, 120, 120];

    let yPos = 15;
    const leftMargin = 15;
    const rightMargin = pageWidth - 15;
    const contentWidth = pageWidth - 30;

    // ===== HEADER =====
    // Red header bar
    doc.setFillColor(...primaryRed);
    doc.rect(0, 0, pageWidth, 35, 'F');

    // Company name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(c.company_name || "AUVERGNE TECH RACING", pageWidth / 2, 15, { align: "center" });

    // Document title
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(c.document_title || "DÉCHARGE DE RESPONSABILITÉ ET D'INFORMATION PRÉALABLE", pageWidth / 2, 25, { align: "center" });

    yPos = 45;

    // ===== COMPANY INFO BOX =====
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(leftMargin, yPos, contentWidth, 20, 2, 2, 'F');

    doc.setTextColor(...darkGray);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(c.company_address || contact.fullAddress || "47 rue Jules Verne, 63100 Clermont-Ferrand", pageWidth / 2, yPos + 8, { align: "center" });
    doc.text(c.company_siren || "SIREN : 914 749 676", pageWidth / 2, yPos + 15, { align: "center" });

    yPos += 28;

    // ===== CLIENT & VEHICLE SECTION =====
    // Two columns layout
    const colWidth = (contentWidth - 10) / 2;

    // Client box - white background
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(leftMargin, yPos, colWidth, 45, 2, 2, 'FD');

    doc.setTextColor(...primaryRed);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(labels.client || "CLIENT", leftMargin + 5, yPos + 8);

    doc.setTextColor(...black);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Nom : ${discharge.client_name || "___________________"}`, leftMargin + 5, yPos + 18);
    doc.text(`Tél : ${discharge.client_phone || "___________________"}`, leftMargin + 5, yPos + 26);
    const addrLines = doc.splitTextToSize(`Adresse : ${discharge.client_address || "___________________"}`, colWidth - 10);
    addrLines.forEach((line, i) => {
      doc.text(line, leftMargin + 5, yPos + 34 + (i * 6));
    });

    // Vehicle box - white background (same as client)
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(leftMargin + colWidth + 10, yPos, colWidth, 45, 2, 2, 'FD');

    doc.setTextColor(...primaryRed);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(labels.vehicle || "VÉHICULE", leftMargin + colWidth + 15, yPos + 8);

    doc.setTextColor(...black);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`${discharge.vehicle_brand || ""} ${discharge.vehicle_model || ""}`.trim() || "___________________", leftMargin + colWidth + 15, yPos + 18);
    doc.text(`Immat : ${discharge.vehicle_plate || "___________"}`, leftMargin + colWidth + 15, yPos + 26);
    doc.text(`VIN : ${discharge.vehicle_vin || "___________________"}`, leftMargin + colWidth + 15, yPos + 34);
    doc.text(`Km : ${discharge.vehicle_mileage || "_______"}`, leftMargin + colWidth + 15, yPos + 42);

    yPos += 52;

    // ===== INTERVENTION TYPE =====
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(leftMargin, yPos, contentWidth, 22, 2, 2, 'F');

    doc.setTextColor(...primaryRed);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(labels.intervention || "NATURE DE L'INTERVENTION", leftMargin + 5, yPos + 8);

    doc.setTextColor(...black);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    const hasGain = discharge.intervention_type?.includes("gain_puissance");
    const hasEthanol = discharge.intervention_type?.includes("conversion_ethanol");
    const hasOther = discharge.intervention_type?.includes("autres");

    doc.text(`[${hasGain ? "X" : "  "}] ${labels.intervention_gain || "Gain de puissance"}`, leftMargin + 5, yPos + 17);
    doc.text(`[${hasEthanol ? "X" : "  "}] ${labels.intervention_ethanol || "Conversion éthanol"}`, leftMargin + 65, yPos + 17);
    doc.text(`[${hasOther ? "X" : "  "}] ${labels.intervention_other || "Autres"} : ${discharge.other_intervention || ""}`, leftMargin + 130, yPos + 17);

    yPos += 30;

    // ===== ENGAGEMENTS =====
    doc.setTextColor(...primaryRed);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(labels.engagements_title || "ENGAGEMENTS ET RECONNAISSANCE DU CLIENT", leftMargin, yPos);
    yPos += 6;

    doc.setTextColor(...lightGray);
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text(labels.engagements_intro || "Le client déclare avoir été informé des points suivants et les accepte sans réserve :", leftMargin, yPos);
    yPos += 8;

    // Engagement points with bullet style
    const engagements = (c.engagements && c.engagements.length ? c.engagements : [
      { title: "Homologation", text: "L'intervention modifie les caractéristiques techniques d'origine. Le véhicule n'est plus homologué pour la voie publique et est réservé à un usage circuit/compétition. Toute circulation sur voie publique nécessite une réception à titre isolé (DREAL)." },
      { title: "Assurance", text: "Le client doit informer sa compagnie d'assurance des modifications. Le Prestataire décline toute responsabilité en cas de refus d'indemnisation." },
      { title: "Garantie", text: "Cette modification peut entraîner l'annulation de la garantie constructeur et de toute autre garantie mécanique." },
      { title: "Mécanique", text: "L'optimisation augmente les contraintes sur les organes mécaniques. Le Prestataire décline toute responsabilité en cas de casse découlant d'un défaut d'entretien ou de la vétusté." },
      { title: "Retour origine", text: "Le client a été informé de la possibilité ou non de remettre le véhicule dans sa configuration d'origine et des frais associés." }
    ]);
    
    engagements.forEach(eng => {
      if (yPos > 255) {
        doc.addPage();
        yPos = 20;
      }
      
      // Bullet point
      doc.setFillColor(...primaryRed);
      doc.circle(leftMargin + 2, yPos - 1, 1.5, 'F');
      
      doc.setTextColor(...darkGray);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(eng.title + " :", leftMargin + 6, yPos);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      const lines = doc.splitTextToSize(eng.text, contentWidth - 10);
      yPos += 5;
      lines.forEach(line => {
        doc.text(line, leftMargin + 6, yPos);
        yPos += 4;
      });
      yPos += 3;
    });
    
    // ===== SIGNATURE SECTION =====
    yPos = Math.max(yPos + 5, 220);

    doc.setDrawColor(200, 200, 200);
    doc.line(leftMargin, yPos, rightMargin, yPos);
    yPos += 8;

    doc.setTextColor(...black);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const cityForSig = c.signature_city || "Clermont-Ferrand";
    const dateForSig = discharge.signature_date || "____/____/________";
    const signatureLineTpl = labels.signature_line || "Fait à {city}, le {date}";
    const signatureLine = signatureLineTpl.replace(/\{city\}/g, cityForSig).replace(/\{date\}/g, dateForSig);
    doc.text(signatureLine, leftMargin, yPos);
    doc.text(labels.signature_copies || "Fait en deux exemplaires originaux.", pageWidth / 2 + 20, yPos);
    yPos += 12;

    // Signature boxes
    const boxWidth = 75;
    const boxHeight = 40;

    doc.setDrawColor(180, 180, 180);
    doc.roundedRect(leftMargin, yPos, boxWidth, boxHeight, 2, 2, 'D');
    doc.roundedRect(rightMargin - boxWidth, yPos, boxWidth, boxHeight, 2, 2, 'D');

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...darkGray);
    doc.text(labels.signature_client || "Signature du client", leftMargin + boxWidth/2, yPos + 6, { align: "center" });
    doc.text(labels.signature_provider || "Signature du Prestataire", rightMargin - boxWidth/2, yPos + 6, { align: "center" });

    doc.setFont("helvetica", "italic");
    doc.setFontSize(6);
    doc.setTextColor(...lightGray);
    const mention = labels.signature_mention || '"Lu et approuvé, bon pour accord"';
    doc.text(mention, leftMargin + boxWidth/2, yPos + 12, { align: "center" });
    doc.text(labels.signature_stamp || "(Cachet)", rightMargin - boxWidth/2, yPos + 12, { align: "center" });

    if (discharge.signed) {
      doc.setFontSize(14);
      doc.setTextColor(34, 197, 94);
      doc.setFont("helvetica", "bold");
      doc.text(labels.signed_stamp || "SIGNÉ ✓", leftMargin + boxWidth/2, yPos + 28, { align: "center" });
    }
    
    // Save
    doc.save(`decharge_${discharge.vehicle_plate.replace(/[^a-zA-Z0-9]/g, '_')}_${discharge.client_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
    toast.success("PDF généré !");
  };

  // Generate PDF for vehicle intake
  const generateIntakePDF = async (intake) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Colors
    const primaryRed = [255, 59, 48];
    const darkGray = [30, 30, 30];
    const lightGray = [100, 100, 100];
    const white = [255, 255, 255];
    
    // Header background
    doc.setFillColor(...primaryRed);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Company name centered in header
    doc.setTextColor(...white);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("ATELIER AUVERGNE TECH RACING", pageWidth / 2, 20, { align: "center" });
    
    // Subtitle
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Fiche de Prise en Charge Véhicule", pageWidth / 2, 32, { align: "center" });
    
    // Status badge on right
    const statusText = intake.status === 'completed' ? 'TERMINÉ' : 
                       intake.status === 'in_progress' ? 'EN COURS' : 'RÉCEPTION';
    const statusColor = intake.status === 'completed' ? [34, 197, 94] : 
                        intake.status === 'in_progress' ? [234, 179, 8] : [59, 130, 246];
    
    // Status badge background
    doc.setFillColor(...statusColor);
    doc.roundedRect(pageWidth - 45, 50, 35, 10, 2, 2, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(statusText, pageWidth - 27.5, 57, { align: "center" });
    
    let yPos = 65;
    const leftMargin = 20;
    const rightCol = 110;
    const lineHeight = 7;
    const sectionGap = 12;
    
    // Helper function for section header
    const addSectionHeader = (title) => {
      doc.setFillColor(245, 245, 245);
      doc.rect(leftMargin - 5, yPos - 5, pageWidth - 30, 8, 'F');
      doc.setTextColor(...primaryRed);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(title, leftMargin, yPos);
      yPos += 10;
    };
    
    // Helper function for field
    const addField = (label, value, x = leftMargin) => {
      if (value) {
        doc.setTextColor(...lightGray);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(label, x, yPos);
        yPos += 4;
        doc.setTextColor(...darkGray);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(value, x === leftMargin ? 80 : 80);
        lines.forEach(line => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, x, yPos);
          yPos += lineHeight - 2;
        });
        yPos += 2;
      }
    };
    
    // Two-column field
    const addTwoColFields = (label1, value1, label2, value2) => {
      const startY = yPos;
      if (value1) {
        addField(label1, value1, leftMargin);
      }
      const afterFirstY = yPos;
      yPos = startY;
      if (value2) {
        addField(label2, value2, rightCol);
      }
      yPos = Math.max(afterFirstY, yPos);
    };
    
    // SECTION: Informations Véhicule
    addSectionHeader("INFORMATIONS VÉHICULE");
    addTwoColFields("Plaque", intake.plate, "Kilométrage", intake.mileage ? `${intake.mileage.toLocaleString()} km` : '-');
    addTwoColFields("Marque", intake.brand || '-', "Modèle", intake.model || '-');
    addField("Niveau carburant", intake.fuel_level || '-');
    yPos += sectionGap;
    
    // SECTION: Client (if exists)
    if (intake.client_name || intake.client_phone) {
      addSectionHeader("INFORMATIONS CLIENT");
      addTwoColFields("Nom", intake.client_name || '-', "Téléphone", intake.client_phone || '-');
      yPos += sectionGap;
    }
    
    // SECTION: État à la réception
    if (intake.exterior_condition || intake.interior_condition || intake.items_in_vehicle || intake.initial_observations) {
      addSectionHeader("ÉTAT À LA RÉCEPTION");
      if (intake.exterior_condition) addField("État extérieur", intake.exterior_condition);
      if (intake.interior_condition) addField("État intérieur", intake.interior_condition);
      if (intake.items_in_vehicle) addField("Objets dans le véhicule", intake.items_in_vehicle);
      if (intake.initial_observations) addField("Observations initiales", intake.initial_observations);
      yPos += sectionGap;
    }
    
    // SECTION: Intervention
    if (intake.work_performed || intake.recommendations || intake.next_service_suggestion || intake.final_observations) {
      addSectionHeader("INTERVENTION");
      if (intake.work_performed) addField("Travaux effectués", intake.work_performed);
      if (intake.recommendations) addField("Recommandations", intake.recommendations);
      if (intake.next_service_suggestion) addField("Prochaine révision conseillée", intake.next_service_suggestion);
      if (intake.final_observations) addField("Observations finales", intake.final_observations);
      yPos += sectionGap;
    }
    
    // Footer section with dates and technician
    yPos = Math.max(yPos, 250);
    doc.setDrawColor(200, 200, 200);
    doc.line(leftMargin, yPos, pageWidth - leftMargin, yPos);
    yPos += 8;
    
    doc.setTextColor(...lightGray);
    doc.setFontSize(9);
    doc.text(`Technicien: ${intake.technician_name}`, leftMargin, yPos);
    doc.text(`Créé le: ${new Date(intake.created_at).toLocaleDateString('fr-FR')}`, rightCol, yPos);
    if (intake.completed_at) {
      yPos += 6;
      doc.text(`Terminé le: ${new Date(intake.completed_at).toLocaleDateString('fr-FR')}`, rightCol, yPos);
    }
    
    // Save PDF
    doc.save(`fiche_${intake.plate.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF généré avec succès !");
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.id.trim() || !categoryForm.name.trim()) {
      toast.error("L'identifiant et le nom sont requis");
      return;
    }
    
    // Normalize ID (lowercase, no spaces)
    const normalizedId = categoryForm.id.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    
    setCategoryLoading(true);
    try {
      const categoryData = {
        ...categoryForm,
        id: normalizedId
      };
      
      if (editingCategory) {
        await axios.put(`${API}/admin/shop/categories/${editingCategory.id}`, categoryData);
        toast.success("Catégorie mise à jour");
      } else {
        await axios.post(`${API}/admin/shop/categories`, categoryData);
        toast.success("Catégorie créée");
      }
      
      fetchShopCategories();
      setCategoryForm({ id: "", name: "", icon: "Package" });
      setEditingCategory(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette catégorie ?")) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/shop/categories/${categoryId}`);
      toast.success("Catégorie supprimée");
      fetchShopCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus, tracking = "", carrier = "") => {
    setOrderStatusLoading(true);
    try {
      const formData = new FormData();
      formData.append("status", newStatus);
      if (tracking) {
        formData.append("tracking_number", tracking);
      }
      if (carrier) {
        formData.append("carrier", carrier);
      }
      
      await axios.put(`${API}/admin/shop/orders/${orderId}/status`, formData);
      toast.success(newStatus === "shipped" ? "Commande expédiée ! Email envoyé au client." : "Statut mis à jour");
      fetchShopOrders();
      setSelectedOrder(null);
      setTrackingNumber("");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setOrderStatusLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette commande ? Cette action est irréversible.")) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/shop/orders/${orderId}`);
      toast.success("Commande supprimée");
      fetchShopOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Quotes functions
  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/admin/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error("Error fetching quotes:", error);
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    if (!quoteForm.customer_name || !quoteForm.customer_email || !quoteForm.description) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    // Check that at least one amount is provided
    if (!quoteForm.amount_ht && !quoteForm.amount_ttc) {
      toast.error("Veuillez saisir le montant HT ou TTC");
      return;
    }
    
    setQuoteLoading(true);
    try {
      const formData = new FormData();
      Object.entries(quoteForm).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      
      // Add PDF file if selected
      if (quotePdfFile) {
        formData.append("quote_pdf", quotePdfFile);
      }
      
      await axios.post(`${API}/admin/quotes`, formData);
      toast.success("Devis créé avec succès !");
      setQuoteForm({
        customer_name: "", customer_email: "", customer_phone: "",
        description: "", vehicle_info: "", amount_ht: "", amount_ttc: "",
        external_quote_number: "", notes: ""
      });
      setQuotePdfFile(null);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création du devis");
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleSendQuote = async (quoteId) => {
    if (!window.confirm("Envoyer le lien de paiement par email au client ?")) return;
    try {
      await axios.post(`${API}/admin/quotes/${quoteId}/send`);
      toast.success("Lien de paiement envoyé par email !");
      fetchQuotes();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  const handleCreateAppointmentFromQuote = async () => {
    if (!showCreateAppointmentFromQuote) return;
    if (!appointmentFromQuoteForm.date || !appointmentFromQuoteForm.time || !appointmentFromQuoteForm.service) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("date", appointmentFromQuoteForm.date);
      formData.append("time", appointmentFromQuoteForm.time);
      formData.append("service", appointmentFromQuoteForm.service);
      formData.append("duration", appointmentFromQuoteForm.duration);
      
      await axios.post(`${API}/admin/quotes/${showCreateAppointmentFromQuote.id}/create-appointment`, formData);
      toast.success("Rendez-vous créé avec succès !");
      setShowCreateAppointmentFromQuote(null);
      setAppointmentFromQuoteForm({ date: "", time: "", service: "", duration: 60 });
      fetchQuotes();
      fetchAppointments();
    } catch (error) {
      toast.error("Erreur lors de la création du RDV");
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm("Supprimer ce devis ?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`);
      toast.success("Devis supprimé");
      fetchQuotes();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleAddShopImage = () => {
    if (shopImageUrl.trim()) {
      setShopProductForm(prev => ({
        ...prev,
        images: [...prev.images, shopImageUrl.trim()]
      }));
      setShopImageUrl("");
    }
  };

  const handleRemoveShopImage = (index) => {
    setShopProductForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleAddSpec = () => {
    if (shopSpecKey.trim() && shopSpecValue.trim()) {
      setShopProductForm(prev => ({
        ...prev,
        specifications: { ...prev.specifications, [shopSpecKey.trim()]: shopSpecValue.trim() }
      }));
      setShopSpecKey("");
      setShopSpecValue("");
    }
  };

  const handleRemoveSpec = (key) => {
    setShopProductForm(prev => {
      const newSpecs = { ...prev.specifications };
      delete newSpecs[key];
      return { ...prev, specifications: newSpecs };
    });
  };

  const handleSaveProduct = async () => {
    if (!shopProductForm.name || !shopProductForm.price) {
      toast.error("Nom et prix requis");
      return;
    }

    setShopLoading(true);
    try {
      const productData = {
        ...shopProductForm,
        price: parseFloat(shopProductForm.price) || 0,
        original_price: parseFloat(shopProductForm.original_price) || 0,
        shipping_cost: parseFloat(shopProductForm.shipping_cost) || 0,
        stock: parseInt(shopProductForm.stock) || 0
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/shop/products/${editingProduct.id}`, productData);
        toast.success("Produit mis à jour");
      } else {
        await axios.post(`${API}/admin/shop/products`, productData);
        toast.success("Produit créé");
      }

      setShopProductForm({
        name: "", description: "", short_description: "", category: "turbos",
        brand: "", reference: "", price: "", original_price: "", shipping_cost: "", stock: "",
        images: [], specifications: {}, featured: false, active: true, show_stock: true
      });
      setEditingProduct(null);
      fetchShopProducts();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setShopLoading(false);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShopProductForm({
      name: product.name || "",
      description: product.description || "",
      short_description: product.short_description || "",
      category: product.category || "turbos",
      brand: product.brand || "",
      reference: product.reference || "",
      price: product.price?.toString() || "",
      original_price: product.original_price?.toString() || "",
      shipping_cost: product.shipping_cost?.toString() || "",
      stock: product.stock?.toString() || "",
      images: product.images || [],
      specifications: product.specifications || {},
      featured: product.featured || false,
      active: product.active !== false,
      show_stock: product.show_stock !== false  // Default to true if not set
    });
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/admin/shop/products/${productId}`);
      toast.success("Produit supprimé");
      fetchShopProducts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleImportPreview = async () => {
    if (!importUrl.trim()) {
      toast.error("Veuillez entrer une URL");
      return;
    }

    setImportLoading(true);
    setImportVariants(null);
    setSelectedVariant("");
    
    try {
      const formData = new FormData();
      formData.append("url", importUrl.trim());
      
      const response = await axios.post(`${API}/admin/shop/import-preview`, formData);
      const data = response.data;
      
      if (data.has_variants && data.variants && data.variants.length > 0) {
        // Product has variants, show selection
        setImportVariants(data);
        toast.info(`${data.variants.length} version(s) disponible(s). Sélectionnez une version.`);
      } else {
        // No variants, proceed with direct import
        await handleImportFromUrl();
      }
    } catch (error) {
      console.error("Preview error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'aperçu");
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportFromUrl = async (variantOverride = null) => {
    if (!importUrl.trim()) {
      toast.error("Veuillez entrer une URL");
      return;
    }

    const variantToUse = variantOverride || selectedVariant;

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append("url", importUrl.trim());
      if (variantToUse) {
        formData.append("selected_variant", variantToUse);
      }
      
      const response = await axios.post(`${API}/admin/shop/import-from-url`, formData);
      const data = response.data;
      
      setShopProductForm({
        name: data.name || "",
        description: data.description || "",
        short_description: data.short_description || "",
        category: data.category || "turbos",
        brand: data.brand || "",
        reference: data.reference || "",
        price: data.price?.toString() || "",
        original_price: data.original_price?.toString() || "",
        stock: "",
        images: data.images || [],
        specifications: data.specifications || {},
        featured: false,
        active: true,
        show_stock: true
      });
      
      setImportUrl("");
      setImportVariants(null);
      setSelectedVariant("");
      toast.success("Produit importé ! Vérifiez et complétez les informations.");
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImportLoading(false);
    }
  };

  const handleDynoFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setDynoLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/dyno/parse`, formData);
      setDynoDataPoints(response.data.data_points);
      setDynoPeaks({
        max_power: response.data.max_power,
        max_power_rpm: response.data.max_power_rpm,
        max_torque: response.data.max_torque,
        max_torque_rpm: response.data.max_torque_rpm
      });
      
      // Auto-fill atmospheric data from file if available
      if (response.data.estimated_pressure) {
        setDynoForm(prev => ({
          ...prev,
          pressure: response.data.estimated_pressure,
        }));
      }
      
      setDynoFileUploaded(true);
      toast.success(`Fichier analysé: ${response.data.total_points} points | Altitude: ${response.data.estimated_altitude || 0}m | Pression estimée: ${response.data.estimated_pressure || 1013} hPa`);
    } catch (error) {
      toast.error("Erreur lors de l'analyse du fichier");
    } finally {
      setDynoLoading(false);
    }
  };

  const handleCreateDynoRun = async () => {
    if (!dynoForm.vehicle_brand || !dynoForm.vehicle_model || dynoDataPoints.length === 0) {
      toast.error("Veuillez remplir les infos véhicule et importer un fichier de données");
      return;
    }

    setDynoLoading(true);
    try {
      const payload = {
        ...dynoForm,
        data_points: dynoDataPoints,
        ...dynoPeaks
      };
      const response = await axios.post(`${API}/admin/dyno`, payload);
      toast.success("Passage dyno enregistré");
      await fetchDynoRuns();
      resetDynoForm();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setDynoLoading(false);
    }
  };

  const resetDynoForm = () => {
    setDynoForm({
      vehicle_brand: "",
      vehicle_model: "",
      vehicle_year: "",
      vehicle_plate: "",
      config_name: "",
      temperature: 20,
      pressure: 1013,
      humidity: 50,
      correction_norm: "DIN 70020",
      correction_coefficient: 1.0,
      client_email: "",
      client_name: "",
      client_phone: "",
      run_date: new Date().toISOString().split('T')[0],
      notes: ""
    });
    setDynoDataPoints([]);
    setDynoPeaks({ max_power: 0, max_power_rpm: 0, max_torque: 0, max_torque_rpm: 0 });
    setDynoFileUploaded(false);
    if (dynoFileInputRef.current) dynoFileInputRef.current.value = "";
  };

  const handleDeleteDynoRun = async (id) => {
    if (!window.confirm("Supprimer ce passage dyno ?")) return;
    try {
      await axios.delete(`${API}/admin/dyno/${id}`);
      toast.success("Passage supprimé");
      await fetchDynoRuns();
      if (selectedDynoRun?.id === id) setSelectedDynoRun(null);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Helper function to generate Dyno chart on a canvas - Mi Systems Style
  const generateDynoChartCanvas = async (run) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 1240;
      canvas.height = 1754;
      const ctx = canvas.getContext('2d');
      
      const width = canvas.width;
      const height = canvas.height;
      
      const drawChart = (logoImg) => {
        // White background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);

        // === LAYOUT CONSTANTS ===
        const headerHeight = 90;
        const rightPanelWidth = 280;
        const bottomHeight = 80;
        const graphLeft = 70;
        const graphRight = width - rightPanelWidth - 30;
        const graphTop = headerHeight + 15;
        const graphBottom = height - bottomHeight;
        const graphWidth = graphRight - graphLeft;
        const graphHeight = graphBottom - graphTop;

        // === HEADER SECTION ===
        // Left: ATR Logo
        if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
          const logoHeight = 80;
          const logoWidth = logoHeight * (logoImg.width / logoImg.height);
          ctx.drawImage(logoImg, 15, 5, logoWidth, logoHeight);
        } else {
          ctx.fillStyle = '#e63946';
          ctx.font = 'bold 28px Arial';
          ctx.textAlign = 'left';
          ctx.fillText('AUVERGNE TECH RACING', 15, 45);
          ctx.fillStyle = '#666666';
          ctx.font = '12px Arial';
          ctx.fillText('SPÉCIALISTE REPROGRAMMATION', 15, 62);
        }

        // Center: Company info
        ctx.fillStyle = '#333333';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(contact.business_name || 'Auvergne Tech Racing', width / 2 - rightPanelWidth / 2, 28);
        ctx.font = '12px Arial';
        ctx.fillText(contact.street, width / 2 - rightPanelWidth / 2, 46);
        ctx.fillText(`${contact.postal_code} ${(contact.city || '').toUpperCase()}`, width / 2 - rightPanelWidth / 2, 62);

        // Right header: "MESURE DE PUISSANCE"
        ctx.fillStyle = '#e63946';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'right';
        ctx.fillText('MESURE DE PUISSANCE', width - rightPanelWidth - 40, 45);

        // Header line
        ctx.strokeStyle = '#cccccc';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(10, headerHeight);
        ctx.lineTo(width - 10, headerHeight);
        ctx.stroke();

        // === WATERMARK ===
        ctx.save();
        ctx.globalAlpha = 0.05;
        ctx.fillStyle = '#e63946';
        ctx.font = 'bold 80px Arial';
        ctx.textAlign = 'center';
        ctx.translate(graphLeft + graphWidth / 2, graphTop + graphHeight / 2);
        ctx.rotate(-Math.PI / 6);
        ctx.fillText('ATR', 0, -20);
        ctx.font = 'bold 30px Arial';
        ctx.fillText('TECH RACING', 0, 25);
        ctx.restore();

        // === RIGHT PANEL - RUN DETAILS ===
        const panelX = width - rightPanelWidth - 10;
        const panelY = headerHeight + 10;

        // Main info box
        ctx.strokeStyle = '#dddddd';
        ctx.lineWidth = 1;
        ctx.strokeRect(panelX, panelY, rightPanelWidth, 180);
        
        // Color indicator bar
        ctx.fillStyle = '#e63946';
        ctx.fillRect(panelX, panelY, 6, 180);
        
        // Vehicle info
        ctx.fillStyle = '#333333';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(`${run.vehicle_brand || 'Marque'} ${run.vehicle_model || 'Modèle'}`, panelX + 15, panelY + 25);
        
        // Year
        ctx.font = '11px Arial';
        ctx.fillStyle = '#666666';
        ctx.fillText(run.vehicle_year || '', panelX + 15, panelY + 42);
        
        // Date
        ctx.textAlign = 'right';
        const dateStr = run.run_date ? new Date(run.run_date).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR');
        ctx.fillText(dateStr, panelX + rightPanelWidth - 10, panelY + 25);
        
        // Config name
        ctx.textAlign = 'left';
        ctx.font = '12px Arial';
        ctx.fillStyle = '#e63946';
        ctx.fillText(run.config_name || 'Configuration', panelX + 15, panelY + 62);
        
        // Horizontal separator
        ctx.strokeStyle = '#eeeeee';
        ctx.beginPath();
        ctx.moveTo(panelX + 10, panelY + 75);
        ctx.lineTo(panelX + rightPanelWidth - 10, panelY + 75);
        ctx.stroke();
        
        // Power result
        ctx.fillStyle = '#e63946';
        ctx.font = 'bold 22px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(`${run.max_power?.toFixed(1) || 0} ch`, panelX + 15, panelY + 105);
        ctx.font = '11px Arial';
        ctx.fillStyle = '#666666';
        ctx.fillText(`@ ${run.max_power_rpm || 0} tr/min`, panelX + 15, panelY + 122);
        
        // Torque result
        ctx.fillStyle = '#2196F3';
        ctx.font = 'bold 22px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(`${run.max_torque?.toFixed(1) || 0} Nm`, panelX + rightPanelWidth - 15, panelY + 105);
        ctx.font = '11px Arial';
        ctx.fillStyle = '#666666';
        ctx.fillText(`@ ${run.max_torque_rpm || 0} tr/min`, panelX + rightPanelWidth - 15, panelY + 122);
        
        // Labels
        ctx.fillStyle = '#888888';
        ctx.font = '10px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('PUISSANCE AUX ROUES', panelX + 15, panelY + 145);
        ctx.textAlign = 'right';
        ctx.fillText('COUPLE AUX ROUES', panelX + rightPanelWidth - 15, panelY + 145);
        
        // DIN norm
        ctx.textAlign = 'center';
        ctx.fillStyle = '#888888';
        ctx.font = '10px Arial';
        ctx.fillText('Norme DIN 70020', panelX + rightPanelWidth / 2, panelY + 170);

        // === CONDITIONS BOX ===
        const condBoxY = panelY + 195;
        ctx.strokeStyle = '#dddddd';
        ctx.strokeRect(panelX, condBoxY, rightPanelWidth, 70);
        
        ctx.fillStyle = '#333333';
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('CONDITIONS ATMOSPHÉRIQUES', panelX + 10, condBoxY + 18);
        
        ctx.font = '11px Arial';
        ctx.fillStyle = '#666666';
        ctx.fillText(`Température: ${run.temperature || 20} °C`, panelX + 10, condBoxY + 38);
        ctx.fillText(`Pression: ${run.pressure || 1013} hPa`, panelX + 10, condBoxY + 55);
        ctx.textAlign = 'right';
        ctx.fillText(`Humidité: ${run.humidity || 50} %`, panelX + rightPanelWidth - 10, condBoxY + 38);

        // === GRAPH AREA ===
        ctx.fillStyle = '#f8f8f8';
        ctx.fillRect(graphLeft, graphTop, graphWidth, graphHeight);
        
        ctx.strokeStyle = '#333333';
        ctx.lineWidth = 1;
        ctx.strokeRect(graphLeft, graphTop, graphWidth, graphHeight);

        // Data points
        const dataPoints = run.data_points || [];
        if (dataPoints.length > 0) {
          const rpmValues = dataPoints.map(p => p.rpm);
          const powerValues = dataPoints.map(p => p.power);
          const torqueValues = dataPoints.map(p => p.torque);
          
          const minRpm = Math.floor(Math.min(...rpmValues) / 250) * 250;
          const maxRpm = Math.ceil(Math.max(...rpmValues) / 250) * 250;
          const maxPower = Math.ceil(Math.max(...powerValues) / 20) * 20 + 20;
          const maxTorque = Math.ceil(Math.max(...torqueValues) / 25) * 25 + 25;

          const scaleX = (rpm) => graphLeft + ((rpm - minRpm) / (maxRpm - minRpm)) * graphWidth;
          const scalePower = (p) => graphBottom - (p / maxPower) * graphHeight;
          const scaleTorque = (t) => graphBottom - (t / maxTorque) * graphHeight;

          // Draw detailed grid
          ctx.strokeStyle = '#dddddd';
          ctx.lineWidth = 0.5;

          // Vertical grid (RPM)
          for (let rpm = minRpm; rpm <= maxRpm; rpm += 250) {
            const x = scaleX(rpm);
            ctx.beginPath();
            ctx.moveTo(x, graphTop);
            ctx.lineTo(x, graphBottom);
            ctx.stroke();
          }

          // Horizontal grid
          for (let p = 0; p <= maxPower; p += 10) {
            const y = scalePower(p);
            ctx.beginPath();
            ctx.moveTo(graphLeft, y);
            ctx.lineTo(graphRight, y);
            ctx.stroke();
          }

          // Y-axis labels - Power (ch)
          ctx.fillStyle = '#333333';
          ctx.font = 'bold 10px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('ch', graphLeft - 35, graphTop - 8);
          
          ctx.font = '9px Arial';
          ctx.textAlign = 'right';
          for (let p = 0; p <= maxPower; p += 20) {
            ctx.fillText(p.toString(), graphLeft - 8, scalePower(p) + 3);
          }

          // Y-axis labels - Torque (Nm)
          ctx.font = 'bold 10px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('Nm', graphRight + 30, graphTop - 8);
          
          ctx.font = '9px Arial';
          ctx.textAlign = 'left';
          for (let t = 0; t <= maxTorque; t += 25) {
            ctx.fillText(t.toString(), graphRight + 8, scaleTorque(t) + 3);
          }

          // X-axis labels (RPM)
          ctx.textAlign = 'center';
          ctx.font = '9px Arial';
          for (let rpm = minRpm; rpm <= maxRpm; rpm += 500) {
            ctx.fillText(rpm.toString(), scaleX(rpm), graphBottom + 15);
          }
          ctx.font = 'bold 10px Arial';
          ctx.fillText('tr/min', graphRight + 20, graphBottom + 15);

          // === DRAW CURVES ===
          // Torque curve (BLUE, dashed)
          ctx.strokeStyle = '#2196F3';
          ctx.lineWidth = 2.5;
          ctx.setLineDash([6, 4]);
          ctx.beginPath();
          dataPoints.forEach((point, i) => {
            const x = scaleX(point.rpm);
            const y = scaleTorque(point.torque);
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          });
          ctx.stroke();
          ctx.setLineDash([]);

          // Power curve (RED, solid)
          ctx.strokeStyle = '#e63946';
          ctx.lineWidth = 2.5;
          ctx.beginPath();
          dataPoints.forEach((point, i) => {
            const x = scaleX(point.rpm);
            const y = scalePower(point.power);
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          });
          ctx.stroke();
        }

        // === FOOTER ===
        ctx.fillStyle = '#888888';
        ctx.font = '9px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(dateStr, 15, height - 15);
        
        ctx.textAlign = 'center';
        ctx.fillText('Mesures effectuées sur route avec équipement RACELOGIC', graphLeft + graphWidth / 2, height - 15);
        
        ctx.textAlign = 'right';
        ctx.fillText('© Auvergne Tech Racing', width - 15, height - 15);

        resolve(canvas);
      };

      // Load logo
      const logo = new window.Image();
      logo.crossOrigin = "anonymous";
      logo.onload = () => drawChart(logo);
      logo.onerror = () => drawChart(null);
      logo.src = `${API}/logo`;
    });
  };

  const handleSendDynoEmail = async () => {
    if (!dynoEmailForm.to_email || !dynoEmailForm.subject) {
      toast.error("Email et sujet requis");
      return;
    }

    setDynoLoading(true);
    try {
      // Generate the chart canvas
      toast.info("Génération du PDF...");
      const canvas = await generateDynoChartCanvas(dynoEmailModal);
      
      // Generate PDF from canvas
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
      
      // Get base64 PDF data (without the data URI prefix for Resend)
      const pdfBase64 = pdf.output('datauristring').split(',')[1];
      
      const formData = new FormData();
      formData.append("to_email", dynoEmailForm.to_email);
      formData.append("to_name", dynoEmailForm.to_name);
      formData.append("subject", dynoEmailForm.subject);
      formData.append("message", dynoEmailForm.message);
      formData.append("pdf_data", pdfBase64);
      
      await axios.post(`${API}/admin/dyno/${dynoEmailModal.id}/send-email`, formData);
      toast.success("Email envoyé avec le PDF en pièce jointe");
      setDynoEmailModal(null);
      setDynoEmailForm({ to_email: "", to_name: "", subject: "", message: "" });
    } catch (error) {
      console.error("Email error:", error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setDynoLoading(false);
    }
  };

  const selectClientForDyno = (client) => {
    setDynoForm({
      ...dynoForm,
      client_email: client.client_email,
      client_name: client.client_name,
      client_phone: client.client_phone
    });
  };

  const handleSendReminders = async () => {
    if (!window.confirm("Envoyer les rappels pour les rendez-vous de demain ?")) {
      return;
    }
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/admin/send-reminders`);
      toast.success(response.data.message);
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'envoi des rappels";
      toast.error(message);
    } finally {
      setSendingReminders(false);
    }
  };

  // Phase 2 fetch functions
  const fetchPromotions = async () => {
    try {
      const response = await axios.get(`${API}/admin/promotions`);
      setPromotions(response.data);
    } catch (error) {
      console.error("Error fetching promotions:", error);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/admin/gallery`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchClientHistory = async (email) => {
    try {
      const response = await axios.get(`${API}/admin/clients/${encodeURIComponent(email)}/history`);
      setClientHistory(response.data);
    } catch (error) {
      console.error("Error fetching client history:", error);
      toast.error("Erreur de chargement de l'historique");
    }
  };

  // Promotions handlers
  const handleCreatePromo = async (e) => {
    e.preventDefault();
    if (!promoForm.title || !promoForm.description) {
      toast.error("Titre et description requis");
      return;
    }
    try {
      if (editingPromoId) {
        await axios.put(`${API}/admin/promotions/${editingPromoId}`, promoForm);
        toast.success("Promotion mise à jour");
      } else {
        await axios.post(`${API}/admin/promotions`, promoForm);
        toast.success("Promotion créée");
      }
      setPromoForm({ title: "", description: "", discount: "", valid_until: "", show_banner: true, show_section: true });
      setEditingPromoId(null);
      fetchPromotions();
    } catch (error) {
      toast.error(editingPromoId ? "Erreur lors de la mise à jour" : "Erreur lors de la création");
    }
  };

  // Pre-fill the create form with the clicked promo's values → switch to edit mode.
  const handleEditPromo = (promo) => {
    setPromoForm({
      title: promo.title || "",
      description: promo.description || "",
      discount: promo.discount || "",
      // The <input type="date"> needs YYYY-MM-DD; slice off any time component the API might return.
      valid_until: (promo.valid_until || "").slice(0, 10),
      show_banner: !!promo.show_banner,
      show_section: !!promo.show_section,
    });
    setEditingPromoId(promo.id);
    // Scroll up so the admin sees the pre-filled form immediately.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEditPromo = () => {
    setPromoForm({ title: "", description: "", discount: "", valid_until: "", show_banner: true, show_section: true });
    setEditingPromoId(null);
  };

  const handleTogglePromo = async (id) => {
    try {
      const response = await axios.post(`${API}/admin/promotions/${id}/toggle`);
      toast.success(response.data.is_active ? "Promotion activée" : "Promotion désactivée");
      fetchPromotions();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm("Supprimer cette promotion ?")) return;
    try {
      await axios.delete(`${API}/admin/promotions/${id}`);
      toast.success("Promotion supprimée");
      fetchPromotions();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Gallery handlers
  const handleAddImage = async (e) => {
    e.preventDefault();
    if (!galleryForm.url) {
      toast.error("URL de l'image requise");
      return;
    }
    try {
      await axios.post(`${API}/admin/gallery`, galleryForm);
      toast.success("Image ajoutée");
      setGalleryForm({ url: "", caption: "", order: 0 });
      fetchGallery();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'ajout";
      toast.error(message);
    }
  };

  const handleToggleImage = async (id) => {
    try {
      const response = await axios.post(`${API}/admin/gallery/${id}/toggle`);
      toast.success(response.data.is_active ? "Image activée" : "Image désactivée");
      fetchGallery();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur";
      toast.error(message);
    }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm("Supprimer cette image ?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`);
      toast.success("Image supprimée");
      fetchGallery();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Client notes handlers
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedClientHistory) return;
    try {
      await axios.post(`${API}/admin/clients/${encodeURIComponent(selectedClientHistory)}/notes`, { note: newNote });
      toast.success("Note ajoutée");
      setNewNote("");
      fetchClientHistory(selectedClientHistory);
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la note");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try {
      await axios.delete(`${API}/admin/clients/notes/${noteId}`);
      toast.success("Note supprimée");
      fetchClientHistory(selectedClientHistory);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleSearchClients = async (query) => {
    setClientSearch(query);
    if (query.length < 2) {
      fetchClients();
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/clients/search?q=${encodeURIComponent(query)}`);
      setClients(response.data);
    } catch (error) {
      console.error("Error searching clients:", error);
    }
  };

  // Update client info
  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (!selectedClientHistory) return;
    
    try {
      // Plates: split on comma/space, strip empties — backend will UPPER-case & dedupe.
      const platesList = (editClientForm.plates || "")
        .split(/[,\s]+/)
        .map((p) => p.trim())
        .filter(Boolean);
      const response = await axios.put(
        `${API}/admin/clients/${encodeURIComponent(selectedClientHistory)}/update`,
        {
          new_name: editClientForm.name,
          new_email: editClientForm.email,
          new_phone: editClientForm.phone,
          new_plates: platesList.length > 0 ? platesList : null,
        }
      );
      toast.success(response.data.message);
      setIsEditingClient(false);
      
      // If email changed, update selected client
      if (editClientForm.email && editClientForm.email !== selectedClientHistory) {
        setSelectedClientHistory(editClientForm.email);
        fetchClientHistory(editClientForm.email);
      } else {
        fetchClientHistory(selectedClientHistory);
      }
      fetchClients();
      return response.data.duplicate_warning || [];
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la mise à jour";
      toast.error(message);
      return [];
    }
  };

  const startEditingClient = (client) => {
    const clientData = clients.find(c => c.client_email === client);
    if (clientData) {
      setEditClientForm({
        name: clientData.client_name || "",
        email: clientData.client_email || "",
        phone: clientData.client_phone || "",
        plates: (clientData.plates || []).join(", "),
      });
      setIsEditingClient(true);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    const platesList = (newClientForm.plates || "")
      .split(/[,\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    try {
      await axios.post(`${API}/admin/clients`, {
        client_name: newClientForm.name,
        client_email: newClientForm.email.trim().toLowerCase(),
        client_phone: newClientForm.phone,
        client_plates: platesList,
      });
      toast.success("Client créé");
      setCreatingClient(false);
      setNewClientForm({ name: "", email: "", phone: "", plates: "" });
      fetchClients();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur à la création");
    }
  };

  const handleDeleteClientCard = async (clientEmail) => {
    if (!window.confirm(
      "Supprimer définitivement la fiche de ce client ?\n\n" +
      "Note : son historique de rendez-vous ne sera PAS supprimé."
    )) return;
    try {
      await axios.delete(`${API}/admin/clients/${encodeURIComponent(clientEmail)}`);
      toast.success("Fiche client supprimée");
      if (selectedClientHistory === clientEmail) setSelectedClientHistory(null);
      fetchClients();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur à la suppression");
    }
  };

  const handleUpdateClientPlate = async (clientEmail, newPlate) => {
    try {
      const formData = new FormData();
      formData.append("plates", newPlate.toUpperCase());
      await axios.put(`${API}/admin/clients/${encodeURIComponent(clientEmail)}/plates`, formData);
      toast.success("Plaque mise à jour");
      fetchClients();
      fetchClientHistory(clientEmail);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour de la plaque");
    }
  };

  // Technician management
  const handleAssignTechnician = async (appointmentId, technician) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}/technician?technician=${encodeURIComponent(technician)}`);
      toast.success(`Technicien assigné: ${technician}`);
      fetchAllAppointments();
    } catch (error) {
      toast.error("Erreur lors de l'assignation");
    }
  };

  // Duration management
  const handleUpdateDuration = async (appointmentId, duration) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}/duration?duration=${duration}`);
      toast.success(`Durée mise à jour: ${duration} min`);
      fetchAllAppointments();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Resend confirmation email
  const handleResendConfirmation = async (appointmentId) => {
    try {
      const response = await axios.post(`${API}/admin/appointments/${appointmentId}/resend-confirmation`);
      toast.success(response.data.message);
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(message);
    }
  };

  // Manual appointment creation
  const handleCreateManualAppointment = async (e) => {
    e.preventDefault();
    if (!manualForm.service_name || !manualForm.client_name || !manualForm.client_phone || !manualForm.date || !manualForm.time) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/appointments/manual`, {
        ...manualForm,
        service_id: manualForm.service_id || "custom"
      });
      toast.success(`RDV créé${manualForm.client_email ? ' - Email envoyé' : ''}`);

      // Auto-detect duplicates — surface a non-blocking warning if the server
      // returned candidates. The admin can jump to the Clients tab to merge.
      const warnings = response.data?.duplicate_warning || [];
      if (warnings.length > 0) {
        const reasonLabels = { phone: "téléphone", plate: "plaque", name: "nom" };
        const first = warnings[0];
        const reasonStr = (first.reasons || []).map((r) => reasonLabels[r] || r).join(" + ");
        toast.warning(
          `⚠ Doublon potentiel : ${first.client_name} (${first.client_email}) — ${reasonStr}` +
          (warnings.length > 1 ? ` · +${warnings.length - 1} autre(s)` : ""),
          {
            duration: 15000,
            action: {
              label: "Fusionner",
              onClick: () => { setActiveTab("clients"); fetchClients(); },
            },
          }
        );
      }

      setManualForm({
        service_id: "",
        service_name: "",
        client_name: "",
        client_email: "",
        client_phone: "",
        client_plate: "",
        date: "",
        time: "",
        duration: 60,
        technician: "",
        notes: "",
      });
      fetchAllAppointments();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la création";
      toast.error(message);
    }
  };

  const fetchAllAppointments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/appointments`);
      const sorted = response.data.sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.time.localeCompare(b.time);
      });
      setAppointments(sorted);
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Erreur de chargement des rendez-vous");
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      let url = `${API}/appointments`;
      if (filterDate) {
        url += `?date=${filterDate}`;
      }
      const response = await axios.get(url);
      const sorted = response.data.sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.time.localeCompare(b.time);
      });
      setAppointments(sorted);
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Erreur de chargement des rendez-vous");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBlockedSlots = async () => {
    try {
      const response = await axios.get(`${API}/blocked-slots`);
      const sorted = response.data.sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.time.localeCompare(b.time);
      });
      setBlockedSlots(sorted);
    } catch (error) {
      console.error("Error fetching blocked slots:", error);
    }
  };

  const fetchBlockedSlots = async () => {
    try {
      let url = `${API}/blocked-slots`;
      if (filterDate) {
        url += `?date=${filterDate}`;
      }
      const response = await axios.get(url);
      const sorted = response.data.sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.time.localeCompare(b.time);
      });
      setBlockedSlots(sorted);
    } catch (error) {
      console.error("Error fetching blocked slots:", error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const formData = new FormData();
      formData.append("username", username || "admin");
      formData.append("password", password);
      const res = await axios.post(`${API}/admin/login`, formData);
      const tk = res.data?.token;
      if (!tk) throw new Error("Token absent");
      const r = res.data?.role || "admin";
      const u = res.data?.username || username || "admin";
      localStorage.setItem("atr_admin_token", tk);
      localStorage.setItem("atr_admin_role", r);
      localStorage.setItem("atr_admin_username", u);
      sessionStorage.setItem("atr_admin_auth", "true");
      setRole(r);
      setCurrentUser(u);
      if (r === "secretary") setActiveTab("appointments");
      setIsAuthenticated(true);
      toast.success(`Connecté en tant que ${u}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    const tk = localStorage.getItem("atr_admin_token");
    try {
      if (tk) {
        await axios.post(`${API}/admin/logout`, null, { headers: { "X-Admin-Token": tk } });
      }
    } catch (_) { /* ignore */ }
    localStorage.removeItem("atr_admin_token");
    localStorage.removeItem("atr_admin_role");
    localStorage.removeItem("atr_admin_username");
    sessionStorage.removeItem("atr_admin_auth");
    setIsAuthenticated(false);
  };

  const handleCancelAppointment = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler ce rendez-vous ?")) {
      return;
    }
    try {
      await axios.delete(`${API}/appointments/${id}`);
      toast.success("Rendez-vous annulé");
      fetchAllAppointments();
    } catch (error) {
      toast.error("Erreur lors de l'annulation");
    }
  };

  const handleDeletePermanent = async (id) => {
    if (!window.confirm("⚠️ ATTENTION: Cette action est irréversible. Supprimer définitivement ce rendez-vous ?")) {
      return;
    }
    try {
      await axios.delete(`${API}/appointments/${id}/permanent`);
      toast.success("Rendez-vous supprimé définitivement");
      fetchAllAppointments();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleReconfirm = async (id) => {
    try {
      await axios.put(`${API}/appointments/${id}/reconfirm`);
      toast.success("Rendez-vous remis en attente");
      fetchAllAppointments();
    } catch (error) {
      toast.error("Erreur lors de la reconfirmation");
    }
  };

  const handleSendReviewRequest = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/review-requests/send/${id}`);
      toast.success(`★ Demande d'avis envoyée à ${r.data.email}`);
      fetchAllAppointments();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(msg);
    }
  };

  const handleReschedule = async (id) => {
    // Allow updating just the plate or service without changing date/time
    if (rescheduleForm.date && !rescheduleForm.time) {
      toast.error("Veuillez sélectionner une heure");
      return;
    }
    if (!rescheduleForm.date && rescheduleForm.time) {
      toast.error("Veuillez sélectionner une date");
      return;
    }
    
    try {
      let url = `${API}/appointments/${id}/reschedule?`;
      const params = [];
      
      if (rescheduleForm.date) params.push(`date=${rescheduleForm.date}`);
      if (rescheduleForm.time) params.push(`time=${rescheduleForm.time}`);
      if (rescheduleForm.client_plate) params.push(`client_plate=${encodeURIComponent(rescheduleForm.client_plate)}`);
      if (rescheduleForm.service_id) params.push(`service_id=${rescheduleForm.service_id}`);
      
      url += params.join('&');
      
      await axios.put(url);
      toast.success("Rendez-vous mis à jour");
      setEditingAppointment(null);
      setRescheduleForm({ date: "", time: "", client_plate: "", service_id: "" });
      fetchAllAppointments();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const openRescheduleModal = (apt) => {
    setEditingAppointment(apt);
    setRescheduleForm({ date: apt.date, time: apt.time, client_plate: apt.client_plate || "", service_id: apt.service_id || "" });
  };

  const openEmailModal = (apt) => {
    setEmailModal(apt);
    setEmailForm({ 
      subject: `Auvergne Tech Racing - ${apt.service_name}`, 
      message: "" 
    });
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.message) {
      toast.error("Veuillez remplir le sujet et le message");
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/send-email`, {
        to_email: emailModal.client_email,
        to_name: emailModal.client_name,
        subject: emailForm.subject,
        message: emailForm.message
      });
      toast.success(`Email envoyé à ${emailModal.client_name}`);
      setEmailModal(null);
      setEmailForm({ subject: "", message: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(message);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendDirectEmail = async () => {
    if (!directEmailForm.to_email || !directEmailForm.subject || !directEmailForm.message) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSendingEmail(true);
    try {
      // Use FormData if there's an attachment
      if (emailAttachment) {
        const formData = new FormData();
        formData.append("to_email", directEmailForm.to_email);
        formData.append("to_name", directEmailForm.to_name || directEmailForm.to_email);
        formData.append("subject", directEmailForm.subject);
        formData.append("message", directEmailForm.message);
        formData.append("file", emailAttachment);

        await axios.post(`${API}/admin/send-email-with-attachment`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      } else {
        await axios.post(`${API}/admin/send-email`, {
          to_email: directEmailForm.to_email,
          to_name: directEmailForm.to_name || directEmailForm.to_email,
          subject: directEmailForm.subject,
          message: directEmailForm.message
        });
      }
      toast.success(`Email envoyé à ${directEmailForm.to_name || directEmailForm.to_email}`);
      setDirectEmailForm({ to_email: "", to_name: "", subject: "", message: "" });
      setSelectedClient(null);
      setEmailAttachment(null);
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(message);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleEmailFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Limit file size to 10MB
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Le fichier est trop volumineux (max 10 Mo)");
        return;
      }
      setEmailAttachment(file);
      toast.success(`Fichier "${file.name}" ajouté`);
    }
  };

  const removeEmailAttachment = () => {
    setEmailAttachment(null);
    if (emailFileInputRef.current) {
      emailFileInputRef.current.value = "";
    }
  };

  const selectClientForEmail = (apt) => {
    setSelectedClient(apt);
    setDirectEmailForm({
      to_email: apt.client_email,
      to_name: apt.client_name,
      subject: "",
      message: ""
    });
  };

  // Get unique clients from appointments
  const uniqueClients = appointments.reduce((acc, apt) => {
    if (!acc.find(c => c.client_email === apt.client_email)) {
      acc.push({
        client_name: apt.client_name,
        client_email: apt.client_email,
        client_phone: apt.client_phone,
        service_name: apt.service_name
      });
    }
    return acc;
  }, []);

  const handleConfirmAppointment = async (id, file = null) => {
    setConfirmingId(id);
    try {
      const formData = new FormData();
      if (file) {
        formData.append("file", file);
      }

      await axios.post(`${API}/appointments/${id}/confirm`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Rendez-vous confirmé et email envoyé au client");
      fetchAllAppointments();
    } catch (error) {
      const message =
        error.response?.data?.detail || "Erreur lors de la confirmation";
      toast.error(message);
    } finally {
      setConfirmingId(null);
    }
  };

  const handleFileSelect = (id, event) => {
    const file = event.target.files[0];
    if (file) {
      handleConfirmAppointment(id, file);
    }
  };

  const triggerFileInput = (id) => {
    if (fileInputRef.current[id]) {
      fileInputRef.current[id].click();
    }
  };

  const handleBlockSlot = async (e) => {
    e.preventDefault();
    if (!blockForm.date || !blockForm.time || !blockForm.service_id) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    const selectedService = services.find((s) => s.id === blockForm.service_id);

    try {
      await axios.post(`${API}/blocked-slots`, {
        ...blockForm,
        service_name: selectedService?.name || "Service",
      });
      toast.success("Créneau bloqué avec succès");
      setBlockForm({ date: "", time: "", service_id: "", reason: "Réservation directe" });
      fetchAllBlockedSlots();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors du blocage";
      toast.error(message);
    }
  };

  const handleUnblockSlot = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir débloquer ce créneau ?")) {
      return;
    }
    try {
      await axios.delete(`${API}/blocked-slots/${id}`);
      toast.success("Créneau débloqué");
      fetchAllBlockedSlots();
    } catch (error) {
      toast.error("Erreur lors du déblocage");
    }
  };

  const handleCalendarDateSelect = (date) => {
    setSelectedCalendarDate(date);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
    const months = [
      "Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
      "Juil", "Août", "Sep", "Oct", "Nov", "Déc",
    ];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "cancelled":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case "completed":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "request":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      default:
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "confirmed":
        return "Confirmé";
      case "cancelled":
        return "Annulé";
      case "completed":
        return "Terminé";
      case "request":
        return "À planifier";
      default:
        return "En attente";
    }
  };

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  // Filter appointments: upcoming (today and future) vs completed (past)
  const upcomingAppointments = appointments.filter((apt) => {
    // Include if no date (request status), or date is today or in the future, and not cancelled
    return (!apt.date || apt.date >= today) && apt.status !== "cancelled";
  });

  const completedAppointments = appointments.filter((apt) => {
    // Include if date is in the past, or status is cancelled
    return (apt.date && apt.date < today) || apt.status === "cancelled";
  });

  // Apply status filter to upcoming appointments
  const filteredAppointments = upcomingAppointments.filter((apt) => {
    if (filterStatus === "all") return true;
    return apt.status === filterStatus;
  });

  // Apply status filter to completed appointments  
  const filteredCompletedAppointments = completedAppointments.filter((apt) => {
    if (filterStatus === "all") return true;
    return apt.status === filterStatus;
  });

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black tracking-tighter uppercase italic text-white">
              ATR<span className="text-[#FF3B30]">.</span> ADMIN
            </h1>
            <p className="text-white/60 mt-2">Gestion des rendez-vous</p>
          </div>

          <form
            onSubmit={handleLogin}
            className="bg-[#121212] border border-white/10 p-8 rounded-lg"
          >
            <div className="flex justify-center mb-6">
              <div className="bg-[#FF3B30]/20 p-4 rounded-full">
                <Lock className="text-[#FF3B30]" size={32} />
              </div>
            </div>

            <div className="mb-3">
              <Input
                type="text"
                data-testid="admin-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Nom d'utilisateur"
                autoCapitalize="none"
                autoComplete="username"
                className="bg-[#1C1C1E] border-white/10 text-white h-12 text-center"
              />
            </div>
            <div className="mb-6">
              <Input
                type="password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe"
                autoComplete="current-password"
                className="bg-[#1C1C1E] border-white/10 text-white h-12 text-center"
              />
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              data-testid="admin-login-btn"
              className="w-full bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-[#FF3B30]/50 text-white font-bold uppercase tracking-wider py-3 transition-all"
            >
              {loginLoading ? "Connexion..." : "Connexion"}
            </button>
          </form>

          <div className="text-center mt-6">
            <a
              href="/"
              className="text-white/50 hover:text-[#FF3B30] text-sm transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft size={16} /> Retour au site
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  const adminCtx = {
    openSiteBanner: () => { setContentInitialKey("banner"); setActiveTab("content"); },
    // state
    appointments, upcomingAppointments, completedAppointments,
    loading, filterDate, setFilterDate, filterStatus, setFilterStatus,
    services, technicians, editingAppointment, setEditingAppointment,
    rescheduleForm, setRescheduleForm, emailModal, setEmailModal,
    emailForm, setEmailForm, sendingEmail, confirmingId, fileInputRef,
    timeSlots, durationOptions, clients,
    atelierIntakes, atelierSearch, setAtelierSearch,
    atelierStatusFilter, setAtelierStatusFilter,
    selectedAtelierIntake, setSelectedAtelierIntake,
    technicianPassword, newTechnicianPassword, setNewTechnicianPassword,
    manualForm, setManualForm,
    statistics, statsLoading, sendingReminders,
    uniqueClients, selectedClient, setSelectedClient,
    directEmailForm, setDirectEmailForm, emailFileInputRef, emailAttachment,
    promotions, promoForm, setPromoForm,
    galleryImages, galleryForm, setGalleryForm,
    clientSearch, selectedClientHistory, setSelectedClientHistory, clientHistory,
    isEditingClient, setIsEditingClient, editClientForm, setEditClientForm,
    newNote, setNewNote,
    discharges, dischargeSearch, setDischargeSearch,
    showDischargeForm, setShowDischargeForm, editingDischargeId, setEditingDischargeId,
    dischargeForm, setDischargeForm,
    quotes, quoteForm, setQuoteForm, quoteLoading, quotePdfFile, setQuotePdfFile,
    showCreateAppointmentFromQuote, setShowCreateAppointmentFromQuote,
    appointmentFromQuoteForm, setAppointmentFromQuoteForm,
    dynoForm, setDynoForm, dynoFileInputRef, dynoFileUploaded, dynoLoading,
    dynoDataPoints, dynoPeaks,
    dynoRuns, dynoCompareSelection, setDynoCompareSelection, dynoCompareModal, setDynoCompareModal,
    selectedDynoRun, setSelectedDynoRun, dynoEmailModal, setDynoEmailModal,
    dynoEmailForm, setDynoEmailForm, dynoChartRef, dynoCompareChartRef,
    setClientSearch,
    // Shop state
    shopProducts, shopOrders, shopCategories,
    shopCustomerSearch, setShopCustomerSearch,
    selectedShopCustomer, setSelectedShopCustomer,
    showCategoryManager, setShowCategoryManager,
    categoryForm, setCategoryForm,
    editingCategory, setEditingCategory,
    shopProductForm, setShopProductForm,
    editingProduct, setEditingProduct,
    shopImageUrl, setShopImageUrl,
    shopSpecKey, setShopSpecKey,
    shopSpecValue, setShopSpecValue,
    importUrl, setImportUrl,
    importLoading,
    importVariants, setImportVariants,
    selectedVariant, setSelectedVariant,
    selectedOrder, setSelectedOrder,
    trackingNumber, setTrackingNumber,
    shopLoading, categoryLoading, orderStatusLoading, shopCustomers,
    // handlers
    fetchAllAppointments, fetchAllBlockedSlots,
    handleUpdateDuration, handleAssignTechnician, handleFileSelect, triggerFileInput,
    handleConfirmAppointment, handleResendConfirmation, openEmailModal, openRescheduleModal,
    handleCancelAppointment, handleReconfirm, handleDeletePermanent, handleReschedule,
    handleSendReviewRequest,
    handleSendEmail, formatDate,
    fetchAtelierIntakes, updateTechnicianPassword, generateIntakePDF, deleteAtelierIntake,
    handleCreateManualAppointment,
    fetchStatistics, handleSendReminders,
    selectClientForEmail, handleEmailFileSelect, removeEmailAttachment, handleSendDirectEmail,
    handleCreatePromo, handleTogglePromo, handleDeletePromo, handleEditPromo, handleCancelEditPromo, editingPromoId,
    handleAddImage, handleToggleImage, handleDeleteImage,
    handleSearchClients, fetchClientHistory, startEditingClient, handleUpdateClient,
    handleUpdateClientPlate, handleAddNote, handleDeleteNote,
    // Standalone client CRUD
    creatingClient, setCreatingClient,
    newClientForm, setNewClientForm,
    handleCreateClient, handleDeleteClientCard,
    fetchDischarges, handleCreateDischarge, generateDischargePDF, signDischarge,
    editDischarge, deleteDischarge,
    handleCreateQuote, handleSendQuote, handleDeleteQuote, handleCreateAppointmentFromQuote,
    handleDynoFileUpload, handleCreateDynoRun, resetDynoForm, handleDeleteDynoRun,
    handleSendDynoEmail, selectClientForDyno,
    handleUpdateOrderStatus, handleDeleteOrder,
    handleImportPreview, handleImportFromUrl, handleSaveProduct, handleEditProduct,
    handleDeleteProduct, handleSaveCategory, handleDeleteCategory,
    handleAddShopImage, handleRemoveShopImage, handleAddSpec, handleRemoveSpec,
    fetchShopCustomers,
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="bg-[#121212] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-base sm:text-xl font-black tracking-tighter uppercase italic shrink-0">
              ATR<span className="text-[#FF3B30]">.</span> ADMIN
            </h1>
            <span className="text-white/40 text-sm hidden md:inline">
              Gestion des rendez-vous
            </span>
          </div>

          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <span
              data-testid="admin-current-user"
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[11px] uppercase tracking-wider font-bold ${
                role === "secretary"
                  ? "bg-blue-500/15 text-blue-300 border-blue-500/30"
                  : "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30"
              }`}
            >
              {role === "secretary" ? "Secrétaire" : "Admin"} · {currentUser}
            </span>
            <a
              href="/"
              className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors"
            >
              Voir <span className="hidden sm:inline">le site</span>
            </a>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="text-red-400 hover:text-red-300 text-xs sm:text-sm transition-colors"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 py-4 sm:px-4 sm:py-8">
        {(() => {
          // Data-driven tab config so we can render the exact same set of tabs
          // twice (desktop pills + mobile drawer) without duplicating handlers.
          // Role-based tabs: secretary sees only Planning + Clients.
          const SECRETARY_ALLOWED_TABS = new Set([
            "calendar", "appointments", "completed", "blocked", "clients",
          ]);
          // Reorganised Feb 2026 — daily-workflow first, admin tooling last.
          const TAB_GROUPS = [
            { label: "Planning & Clients", tabs: [
              { id: "calendar",     label: "Calendrier",   icon: CalendarDays, onOpen: null },
              { id: "blocked",      label: "Création RDV", icon: Plus,         onOpen: null },
              { id: "appointments", label: "Rendez-vous",  icon: Calendar,     onOpen: null },
              { id: "completed",    label: "RDV Effectués", icon: CheckCircle, onOpen: null, testid: "admin-tab-completed" },
              { id: "clients",      label: "Clients",      icon: Users,        onOpen: () => fetchClients() },
              { id: "quotes",       label: "Paiement",     icon: FileText,     onOpen: () => { fetchQuotes(); fetchClients(); } },
            ] },
            { label: "Atelier & Vente", tabs: [
              { id: "atelier",      label: "Atelier",         icon: Wrench,   onOpen: () => { fetchAtelierIntakes(); fetchTechnicianPassword(); } },
              { id: "dyno",         label: "Dyno",            icon: Activity, onOpen: () => { fetchDynoRuns(); fetchClients(); } },
              { id: "discharges",   label: "Décharges",       icon: FileText, onOpen: () => { fetchDischarges(); fetchDischargeContent(); } },
              { id: "depot-vente",  label: "Dépôt-Vente",     icon: Car,      onOpen: null, testid: "admin-tab-depot-vente" },
              { id: "techniciens",  label: "Techniciens",     icon: Users,    onOpen: null, testid: "admin-tab-techniciens" },
              { id: "file-service", label: "Service Fichiers", icon: Upload,   onOpen: null },
            ] },
            { label: "Boutique", tabs: [
              { id: "shop",       label: "Shop",         icon: ShoppingBag, onOpen: () => { fetchShopProducts(); fetchShopOrders(); fetchShopCategories(); fetchShopCustomers(); } },
              { id: "statistics", label: "Statistiques", icon: BarChart3,   onOpen: () => fetchStatistics() },
            ] },
            { label: "Contenu & Marketing", tabs: [
              { id: "content",    label: "Contenu",  icon: FileText,  onOpen: () => setContentInitialKey(null), testid: "admin-tab-content" },
              { id: "gallery",    label: "Galerie",  icon: ImageIcon, onOpen: () => fetchGallery() },
              { id: "promotions", label: "Promos",   icon: Tag,       onOpen: () => fetchPromotions() },
              { id: "messages",   label: "Messages", icon: Mail,      onOpen: null },
            ] },
            { label: "Comptes", tabs: [
              { id: "users",      label: "Utilisateurs", icon: Users,    onOpen: null, testid: "admin-tab-users" },
            ] },
          ]
          // Role filtering — secretaries only see their allowed tabs.
          .map((g) => ({ ...g, tabs: role === "secretary" ? g.tabs.filter((t) => SECRETARY_ALLOWED_TABS.has(t.id)) : g.tabs }))
          .filter((g) => g.tabs.length > 0);
          const ALL_TABS = TAB_GROUPS.flatMap((g) => g.tabs);
          const currentTab = ALL_TABS.find((t) => t.id === activeTab) || ALL_TABS[0];
          const selectTab = (t) => {
            setActiveTab(t.id);
            if (t.onOpen) t.onOpen();
            setMobileMenuOpen(false);
          };

          return (
            <>
              {/* ============== MOBILE TAB PICKER (< md) ============== */}
              <div className="md:hidden mb-4">
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  data-testid="admin-mobile-tab-picker"
                  className="w-full flex items-center justify-between bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-left active:scale-[0.99] transition-transform"
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <currentTab.icon size={18} className="text-[#FF3B30] shrink-0" />
                    <span className="truncate">
                      <span className="block text-white/40 text-[10px] uppercase tracking-widest leading-none mb-0.5">Section</span>
                      <span className="block text-white font-bold text-sm uppercase tracking-wide truncate">{currentTab.label}</span>
                    </span>
                  </span>
                  <Menu size={20} className="text-white/60 shrink-0" />
                </button>
              </div>

              {/* Mobile drawer */}
              {mobileMenuOpen && (
                <div
                  className="md:hidden fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="admin-mobile-drawer-overlay"
                >
                  <div
                    className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto bg-[#0F0F0F] border-t border-white/10 rounded-t-2xl"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="admin-mobile-drawer"
                  >
                    <div className="sticky top-0 bg-[#0F0F0F] flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
                      <span className="text-white font-black uppercase tracking-tight text-base">Sections</span>
                      <button
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid="admin-mobile-drawer-close"
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.06] hover:bg-white/[0.1]"
                        aria-label="Fermer"
                      >
                        <X size={18} className="text-white/70" />
                      </button>
                    </div>
                    <div className="px-3 py-3 space-y-4 pb-8">
                      {TAB_GROUPS.map((group) => (
                        <div key={group.label}>
                          <div className="flex items-center gap-2 px-1 mb-1.5">
                            <span className="text-white/40 text-[10px] font-bold uppercase tracking-widest">{group.label}</span>
                            <div className="flex-1 h-px bg-white/[0.06]" />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {group.tabs.map((t) => {
                              const Icon = t.icon;
                              const isActive = activeTab === t.id;
                              return (
                                <button
                                  key={t.id}
                                  onClick={() => selectTab(t)}
                                  data-testid={t.testid || `admin-mobile-tab-${t.id}`}
                                  className={`flex items-center gap-2 px-3 py-3 rounded-md text-left text-sm font-semibold transition-colors ${
                                    isActive
                                      ? "bg-[#FF3B30] text-white shadow-lg shadow-[#FF3B30]/20"
                                      : "bg-[#1C1C1E] text-white/80 hover:bg-[#242426] border border-white/[0.04]"
                                  }`}
                                >
                                  <Icon size={16} className="shrink-0" />
                                  <span className="truncate">{t.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ============== DESKTOP TAB BAR (>= md) ============== */}
              <div className="hidden md:flex flex-wrap gap-3 mb-6">
                {ALL_TABS.map((t) => {
                  const Icon = t.icon;
                  const isActive = activeTab === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => selectTab(t)}
                      data-testid={t.testid || `admin-desktop-tab-${t.id}`}
                      className={`px-5 py-2.5 font-bold uppercase tracking-wider text-sm transition-all ${
                        isActive
                          ? "bg-[#FF3B30] text-white"
                          : "bg-[#121212] text-white/60 hover:text-white border border-white/10"
                      }`}
                    >
                      <Icon size={16} className="inline mr-2 -mt-0.5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </>
          );
        })()}

        {/* APPOINTMENTS TAB */}
        {activeTab === "appointments" && <AppointmentsTab ctx={adminCtx} />}
        {/* COMPLETED APPOINTMENTS TAB */}
        {activeTab === "completed" && <CompletedTab ctx={adminCtx} />}
        {/* STATISTICS TAB */}
        {activeTab === "statistics" && <StatisticsTab ctx={adminCtx} />}
        {/* ATELIER TAB */}
        {activeTab === "atelier" && <AtelierTab ctx={adminCtx} />}
        {/* DEPOT-VENTE TAB */}
        {activeTab === "depot-vente" && <DepotVenteTab />}
        {/* TECHNICIENS TAB */}
        {activeTab === "techniciens" && <TechniciensTab />}
        {/* DISCHARGES TAB */}
        {activeTab === "discharges" && <DischargesTab ctx={adminCtx} />}
        {/* SHOP TAB */}
        {activeTab === "shop" && <ShopTab ctx={adminCtx} />}

        {/* QUOTES TAB */}
        {activeTab === "quotes" && <QuotesTab ctx={adminCtx} />}

        {/* CALENDAR TAB */}
        {activeTab === "calendar" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AdminCalendar
              appointments={appointments}
              blockedSlots={blockedSlots}
              onDateSelect={handleCalendarDateSelect}
              selectedDate={selectedCalendarDate}
            />

            {selectedCalendarDate ? (
              <DayDetail
                date={selectedCalendarDate}
                appointments={appointments}
                blockedSlots={blockedSlots}
                onClose={() => setSelectedCalendarDate(null)}
              />
            ) : (
              <div className="bg-[#121212] border border-white/10 rounded-lg p-8 flex items-center justify-center">
                <p className="text-white/40 text-center">
                  <CalendarDays size={48} className="mx-auto mb-4 opacity-50" />
                  Cliquez sur une date pour voir les détails
                </p>
              </div>
            )}
          </div>
        )}

        {/* BLOCKED SLOTS TAB - Now includes manual appointments */}
        {activeTab === "blocked" && <CreateAppointmentTab ctx={adminCtx} />}
        {/* MESSAGES TAB */}
        {activeTab === "messages" && <MessagesTab ctx={adminCtx} />}
        {/* PROMOTIONS TAB */}
        {activeTab === "promotions" && <PromotionsTab ctx={adminCtx} />}
        {activeTab === "content" && <ContentTab initialKey={contentInitialKey} />}
        {/* GALLERY TAB */}
        {activeTab === "gallery" && <GalleryTab ctx={adminCtx} />}
        {/* CLIENTS TAB */}
        {activeTab === "clients" && <ClientsTab ctx={adminCtx} />}
        {/* DYNO TAB */}
        {activeTab === "dyno" && <DynoTab ctx={adminCtx} DynoChart={DynoChart} DynoCompareChart={DynoCompareChart} />}
        {activeTab === "users" && <UsersTab currentUsername={currentUser} />}
      </main>

      {/* FILE SERVICE TAB - Rendered outside main to avoid nesting issues */}
      {activeTab === "file-service" && (
        <div className="p-6">
          <FileServiceAdmin />
        </div>
      )}
    </div>
  );
};

export default Admin;
